<template>
  <DetailBuyer v-if="isBuyer && product" />
  <div class="harvest-detail" v-if="product && !isBuyer" @click="activeEdge = null">
    <!-- Product Content -->
    <div class="detail-shell">
      <aside class="detail-aside">
        <section class="hero-stage reveal" :style="{ '--reveal-delay': '0ms' }">
          <div class="hero-frame">
            <div class="hero-canvas" @click="openLightbox">
              <template v-if="isVideo">
                <div class="video-wrapper">
                  <video
                    :src="heroSrc"
                    :poster="videoPoster"
                    class="hero-video"
                    muted
                    loop
                    playsinline
                    ref="heroVideoRef"
                  ></video>
                  <button class="video-play-btn" @click.stop="togglePlay">
                    <i class="fa-solid" :class="isPlaying ? 'fa-pause' : 'fa-play'"></i>
                  </button>
                </div>
              </template>
              <NuxtImg
                v-else
                @error="onHeroError"
                :src="currentHeroSrc"
                :alt="displayTitle"
                class="hero-image"
              />
              <div class="hero-frame-overlay" aria-hidden="true">
                <svg class="frame-svg" viewBox="0 0 100 100" fill="none" preserveAspectRatio="none">
                  <rect x="4" y="4" width="92" height="92" rx="2" stroke="rgba(255,255,255,0.35)" stroke-width="0.6"/>
                </svg>
                <img src="/img/sarvban_logo_2.png" alt="" class="frame-logo" />
              </div>
              <div class="hero-veil"></div>

              <div class="hero-tags">
                <span class="tag tag-hot" v-if="product.hot"><i class="fa-solid fa-fire"></i> {{ $t('home.badge_hot') }}</span>
                <span class="tag tag-special" v-if="product.special"><i class="fa-solid fa-star"></i> {{ $t('home.badge_special') }}</span>
                <span class="tag tag-instant" v-if="product.instant"><i class="fa-solid fa-bolt"></i> {{ $t('home.badge_instant') }}</span>
                <span class="tag tag-presale" v-if="isOutOfSeason"><i class="fa-solid fa-calendar-xmark"></i> {{ $t('home.badge_presale') }}</span>
                <span class="tag tag-zara" v-if="product.zara"><i class="fa-solid fa-seedling"></i> {{ $t('market.zara') }}</span>
              </div>


              <template v-if="galleryImages.length > 1">
                <button class="hero-arrow hero-arrow-prev" @click.stop="prevImage" :aria-label="$t('details.prev_image')">
                  <i class="fa-solid fa-chevron-right"></i>
                </button>
                <button class="hero-arrow hero-arrow-next" @click.stop="nextImage" :aria-label="$t('details.next_image')">
                  <i class="fa-solid fa-chevron-left"></i>
                </button>
                <div class="hero-counter">
                  <span class="counter-current">{{ toFa(currentIndex + 1) }}</span>
                  <span class="counter-divider">/</span>
                  <span class="counter-total">{{ toFa(galleryImages.length) }}</span>
                </div>
              </template>
            </div>

            <div class="thumb-reel" v-if="galleryImages.length > 1">
              <button
                v-for="(img, idx) in galleryImages"
                :key="idx"
                class="thumb"
                :class="{ 'is-active': idx === currentIndex }"
                @click="currentIndex = idx"
                :aria-label="$t('details.image_num', { n: idx + 1 })"
              >
                <NuxtImg
                  :src="imgSrc(img, true)"
                  :alt="displayTitle + ' ' + (idx + 1)"
                />
                <span v-if="isVideoFile(img)" class="thumb-video-badge">
                  <i class="fa-solid fa-play"></i>
                </span>
                <span class="thumb-glow"></span>
              </button>
            </div>
          </div>
        </section>


      </aside>

      <main class="detail-main">
        <section class="title-block reveal" :style="{ '--reveal-delay': '80ms' }">
          <div class="title-headline">
            <div class="title-eyebrow">
              <div class="crumb-trail">
                <NuxtLink to="/" class="crumb">{{ $t('details.breadcrumb_home') }}</NuxtLink>
                <template v-for="(cat, idx) in product.cat_path" :key="idx">
                  <NuxtLink :to="`/cat/${cat.value}/${cat.label}`" class="crumb">
                    <i class="fa-solid fa-chevron-left crumb-sep"></i>
                    {{ cat.label}}
                  </NuxtLink>
                </template>
              </div>
            </div>
            <h1 class="title-main">{{ displayTitle }}</h1>
          </div>
          <section class="narrative reveal" :style="{ '--reveal-delay': '40ms' }" v-if="product.description">
            <div class="narrative-body" :class="{ 'is-collapsed': !showFullDesc }">
              <p v-html="parsedDescription"></p>
            </div>
            <button
              v-if="product.description && product.description.length > 150"
              class="narrative-toggle"
              @click="showFullDesc = !showFullDesc"
            >
              {{ showFullDesc ? $t('details.show_less') : $t('details.read_more') }}
              <i class="fa-solid" :class="showFullDesc ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>
          </section>
          <div class="title-meta-row">
            <div class="meta-chip" v-if="product.score">
              <div class="stars">
                <i v-for="i in 5" :key="i" class="fa-solid fa-star" :class="{ 'star-on': i <= Math.round(product.score) }"></i>
              </div>
              <span class="meta-value">{{ toFa(product.score) }}</span>
            </div>
            <div class="meta-chip" v-if="product.address?.city || product.address?.ostan">
              <i class="fa-solid fa-location-dot"></i>
              <span>{{ product.address?.city || product.address?.ostan || $t('details.unknown_location') }}</span>
            </div>
            <div class="meta-chip">
              <i class="fa-solid fa-eye"></i>
              <span>{{ toFa(formatNumber(product.views || 0)) }} {{ $t('details.views') }}</span>
            </div>
            <div class="meta-chip">
              <button class="topbar-pill" @click="openShareModal" :aria-label="$t('details.share')">
                <i class="fa-solid fa-share-nodes"></i>
              </button>
            </div>
            <button class="dock-btn dock-order" @click="goToOrderPage" v-if="!isBuyer">
              <i class="fa-solid fa-cart-shopping"></i>
              <span>{{ $t('details.order') }}</span>
            </button>
          </div>
        </section>
        <!-- edge-triggered product navigation -->
        <template v-if="prevProduct || nextProduct">
          <div class="edge-nav edge-nav--right" :class="{ 'edge-nav--active': activeEdge === 'right' }" @click.stop>
            <div class="edge-nav__trigger" @click.stop="activeEdge = activeEdge === 'right' ? null : 'right'"></div>
            <button v-if="prevProduct" class="edge-nav__btn" @click="goToProduct(prevProduct.invoiceNum)">
              <i class="fa-solid fa-chevron-right"></i>
              <span>{{ $t('details.prev_product') }}</span>
            </button>
          </div>
          <div class="edge-nav edge-nav--left" :class="{ 'edge-nav--active': activeEdge === 'left' }" @click.stop>
            <div class="edge-nav__trigger" @click.stop="activeEdge = activeEdge === 'left' ? null : 'left'"></div>
            <button v-if="nextProduct" class="edge-nav__btn" @click="goToProduct(nextProduct.invoiceNum)">
              <span>{{ $t('details.next_product') }}</span>
              <i class="fa-solid fa-chevron-left"></i>
            </button>
          </div>
        </template>
        <section class="specs reveal" :style="{ '--reveal-delay': '80ms' }" v-if="product.info && product.info.length">
          <div class="section-label">
            <span class="label-mark"></span>
            <h3>{{ isBuyer ? $t('details.product_buyer_specs') : $t('details.product_specs') }}</h3>
          </div>
          <div class="specs-table">
            <div class="spec-row">
              <span class="spec-key">{{ $t('details.product_type') }}</span>
              <NuxtLink :to="`/cat/${lastEndCat?.value}/${lastEndCat?.label}`" class="spec-val spec-link" target="_blank">
                <span class="spec-val">👈{{ lastEndCat?.label }}</span>
                
              </NuxtLink>
            </div>
            <div class="spec-row" v-if="product.location?.shahrestan || product.location?.ostan">
              <span class="spec-key">{{ $t('details.location') }}</span>
              <span class="spec-val">
                <NuxtLink v-if="product.location?.ostan" :to="`/cat/${lastEndCat?.value}/${lastEndCat?.label}?ost_txt=${product.location.ostan?.label}&province=${product.location.ostan?.value}`" class="spec-link" target="_blank">
                  {{product.location?.ostan?.label}} - 
                </NuxtLink>
                  {{product.location?.shahrestan?.label}}
              </span>
            </div>
            <template v-for="([parent, specs], idx) in groupedInfo" :key="idx">
              <div class="spec-row" v-if="parent !== $t('details.unit_key')">
                <span class="spec-key">
                  {{ parent }}
                  <HintCallout v-if="parent === 'درجه کیفی'" :text="$t('details.product_rate_hint')" />
                </span>
                <span class="spec-val">
                  <span v-for="(spec, sidx) in specs" :key="spec.id || sidx">
                    <NuxtLink
                      v-if="parent === 'درجه کیفی' && spec.id"
                      :to="`/cat/${lastEndCat?.value}/${lastEndCat?.label}?kind=${spec.id}`"
                      class="spec-link"
                      target="_blank"
                    >
                      {{ spec.title }}
                    </NuxtLink>
                    <span v-else>{{ spec.title }}</span>
                    <template v-if="sidx < specs.length - 1">، </template>
                  </span>
                </span>
              </div>
            </template>
            <div class="spec-row">
              <span class="spec-key">{{ isBuyer ? $t('details.request_amount') : $t('details.stock_amount') }}</span>
              <span class="spec-val">{{ toFa(formatNumber(product.price?.count || 0)) }} {{ vahed }}</span>
            </div>
            <div class="spec-row">
              <span class="spec-key">{{ $t('details.min_order') }}</span>
              <span class="spec-val">
                {{ product.sRetail ?  $t('details.retail') : $t('details.none') }}
              </span>
            </div>
            <div class="spec-row">
              <span class="spec-key">{{ isBuyer ? $t('details.request_code') : $t('details.product_code') }}</span>
              <span class="spec-val">{{ toFa(product.invoiceNum || 0) }}</span>
            </div>
          </div>
        </section>

        <section class="price-card reveal" :style="{ '--reveal-delay': '160ms' }">
          <div class="price-card-bg" aria-hidden="true"></div>
          <div class="price-card-inner">
            <div class="price-headline">
              <span class="price-eyebrow">{{ isBuyer ? $t('details.buyer_price') : $t('details.seller_price') }}</span>
              <div class="price-figure">
                <span class="price-amount">{{ toFa(formatNumber(product.price?.price || 0)) }}</span>
                <span class="price-currency"><svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="none" style="transform: scale(1.5); margin-right:10px;"><path fill="#fff" d="M9.239 4.82c0 .427-.07.827-.208 1.2-.13.38-.319.71-.564.99-.246.279-.546.496-.9.652-.353.163-.749.244-1.187.244h-.703c-.86 0-1.529-.268-2.005-.803-.476-.536-.715-1.27-.715-2.201V2.84h.934v2.026c0 .605.15 1.094.45 1.467.3.373.79.559 1.475.559h.507c.346 0 .641-.054.887-.163.254-.109.461-.256.623-.443.161-.186.28-.403.357-.652.077-.248.115-.508.115-.78V1.723h.934V4.82ZM6.496 1.501h-1.13V.5h1.13v1.001ZM2.067 13.682c-.207 0-.407-.027-.599-.082a1.305 1.305 0 0 1-.507-.302 1.53 1.53 0 0 1-.334-.548A2.545 2.545 0 0 1 .5 11.89V6.462h.945v5.217c0 .295.062.532.184.71.131.179.335.268.611.268h.23c.17 0 .254.167.254.5 0 .35-.084.525-.253.525h-.404Z"></path><path fill="#fff" d="M2.624 12.657c.584 0 .876-.248.876-.745v-.361c0-.66.169-1.18.507-1.56.346-.389.822-.583 1.43-.583.314 0 .59.055.829.163.238.101.438.245.6.431.16.186.28.412.357.676.084.256.126.54.126.85 0 .683-.173 1.215-.518 1.595-.346.373-.822.559-1.43.559a2.32 2.32 0 0 1-.887-.175 1.349 1.349 0 0 1-.645-.617 1.41 1.41 0 0 1-.254.408c-.1.1-.204.182-.311.244a1.3 1.3 0 0 1-.346.117 2.508 2.508 0 0 1-.334.023h-.15c-.1 0-.165-.039-.196-.117a.942.942 0 0 1-.057-.372c0-.186.019-.322.057-.408.03-.085.096-.128.196-.128h.15Zm3.803-1.06c0-.341-.077-.62-.23-.838-.146-.217-.407-.326-.784-.326-.346 0-.603.105-.772.314-.162.21-.242.505-.242.886 0 .341.092.597.276.768.193.17.435.256.727.256.345 0 .603-.09.772-.268.169-.186.253-.45.253-.791ZM9.638 15.487c.315 0 .591-.05.83-.152.238-.093.434-.22.587-.384.162-.163.285-.353.37-.57.084-.218.134-.45.15-.7h-.98c-.392 0-.715-.042-.969-.127a1.372 1.372 0 0 1-.599-.373 1.416 1.416 0 0 1-.311-.594 3.69 3.69 0 0 1-.08-.803c0-.295.041-.575.126-.839.084-.271.207-.508.369-.71.161-.202.36-.36.599-.477.246-.125.53-.187.853-.187.253 0 .495.043.726.128.238.086.446.222.622.408.177.179.315.415.415.71.108.295.162.652.162 1.072v.768h1.025c.093 0 .154.043.185.128.038.078.057.202.057.373 0 .178-.019.31-.057.396-.03.085-.092.128-.185.128h-1.048a3.21 3.21 0 0 1-.23 1.083 2.74 2.74 0 0 1-.554.897 2.565 2.565 0 0 1-.865.605 2.674 2.674 0 0 1-1.129.233H8.508l-.069-1.013h1.199Zm-.104-3.761c0 .333.077.574.23.722.162.14.47.21.923.21h.91v-.723c0-.489-.096-.838-.288-1.048-.184-.217-.446-.326-.784-.326-.315 0-.56.105-.737.315-.17.201-.254.485-.254.85Z"></path><path fill="#fff" d="M14.587 12.657c.276 0 .492-.074.645-.221.162-.148.242-.357.242-.629v-1.49h.945v1.537c0 .59-.157 1.044-.472 1.362-.308.31-.738.466-1.291.466h-1.118c-.1 0-.165-.039-.196-.117a.942.942 0 0 1-.058-.372c0-.186.02-.322.058-.408.03-.085.096-.128.196-.128h1.049ZM16.5 8.884h-1.049v-.978H16.5v.978Zm-1.544 0h-1.05v-.978h1.05v.978Z"></path></svg></span>
              </div>
              <div class="locale-currency-price" v-if="!isExportable && !currencyLoading && (priceInUSD)"> 
                <span class="locale-currency-label">{{ $t('details.price_usd') }}</span>
                <span class="locale-currency-value">$ {{ toFa(formatNumber(priceInUSD)) }}</span>
              </div>
              <div class="price-sub" v-if="updateLabel">
                {{ updateLabel }}
                
              </div>
            </div>
            
            <div class="price-side">
              <div class="price-status" :class="statusClass" v-html="statusText"></div>
              <div class="price-updated" v-if="product.date">
                <i class="fa-regular fa-clock"></i>
                {{ toFa(daysPassed) }} {{ $t('details.days_ago') }}
              </div>
            </div>
          </div>
          <div class="price-chart" v-if="!isBuyer">
            <MarketPriceChart
              v-if="lastEndCat?.value"
              :categoryValue="lastEndCat.value"
              :currentPrice="Number(product.price?.price || 0)"
              />
            </div>

          <!-- Exportable Products Currency Conversion -->
          <div class="exportable-currency" v-if="isExportable">
            <div class="exportable-badge">
              <i class="fa-solid fa-globe"></i>
              <span>{{ $t('details.exportable_products') }}</span>
            </div>
            <div class="currency-conversion" v-if="!currencyLoading && (priceInUSD || priceInIQD || priceInRUB)">
              <div class="conversion-row" v-if="priceInUSD">
                <span class="conversion-label">{{ $t('details.price_usd') }}</span>
                <span class="conversion-value">
                  <span class="currency-flag">🇺🇸</span>
                   $ {{ toFa(formatNumber(priceInUSD)) }}
                </span>
              </div>
              <div class="conversion-row" v-if="priceInIQD">
                <span class="conversion-label">{{ $t('details.price_iqd') }}</span>
                <span class="conversion-value">
                  <span class="currency-flag">🇮🇶</span>
                   (IQD) {{ toFa(formatNumber(priceInIQD)) }}
                </span>
              </div>
              <div class="conversion-row" v-if="priceInRUB">
                <span class="conversion-label">{{ $t('details.price_rub') }}</span>
                <span class="conversion-value">
                  <span class="currency-flag">🇷🇺</span>
                  (RUB) {{ toFa(formatNumber(priceInRUB)) }} 
                </span>
              </div>
            </div>
            <div class="currency-loading" v-if="currencyLoading">
              <i class="fa-solid fa-spinner fa-spin"></i>
              <span>{{ $t('details.fetching_rates') || 'در حال دریافت نرخ ارز...' }}</span>
            </div>
            <div class="currency-error" v-if="currencyError">
              <i class="fa-solid fa-triangle-exclamation"></i>
              <span>{{ currencyError }}</span>
            </div>
          </div>

          <div class="price-note">
              <i class="fa-solid fa-circle-info"></i>
              <p>{{$t('details.price_note_2') }}</p>
            </div>

        </section>



      </main>

      <div class="asside-section">
        <section class="origin-story" v-if="product.user">
          <div class="origin-author-top">
            <div class="author-avatar">
              <NuxtImg v-if="product.user?.avatar" :src="product.user.avatar" :alt="product.username" />
              <span v-else>{{ (product.username || $t('details.seller_type'))[0] }}</span>
            </div>
            <div class="author-meta">
              <span class="author-name">{{  isBuyer ? $t('details.buyer_label') : $t('details.seller_label') }}{{ product.username }}</span>
              <span class="author-role">{{ userType }}</span>
            </div>
            <button class="origin-call origin-report" @click="showReportModal = true">
              <i class="fa-solid fa-flag"></i>
            </button>
          </div>

          <p class="origin-quote">
            {{ $t('details.origin_i') }} <strong>{{ product.username }}</strong>, {{ userType }} {{ $t('details.origin_am') }}
            {{ cleanTitle }} {{ $t('details.origin_quality') }} {{ product.address?.ostan }}, {{ product.address?.city }}
            {{ $t('details.origin_offer') }} <br/>{{ $t('details.origin_cta') }}
          </p>
          <div v-show="!isBuyer" class="origin-author">
            <button class="origin-call" @click="ensureAuth('buyNumber') ? (showBuyNumberModal = true) : null">
              <i class="fa-solid fa-phone"></i>
              {{ $t('details.contact_seller') }}
            </button>
          </div>

          <div class="origin-stats" v-if="product.satisfaction">
            <div class="ostat">
              <span class="ostat-num">{{ toFa(product.satisfaction) }}<small>%</small></span>
              <span class="ostat-label">{{ $t('details.satisfaction') }}</span>
            </div>
            <div class="ostat-sep"></div>
            <div class="ostat">
              <span class="ostat-num">{{ toFa(formatNumber(product.visit || 0)) }}</span>
              <span class="ostat-label">{{ $t('details.visits') }}</span>
            </div>
            <div class="ostat-sep"></div>
            <div class="ostat" v-if="product.date">
              <span class="ostat-num">{{ toFa(daysPassed) }}</span>
              <span class="ostat-label">{{ $t('details.days_ago') }}</span>
            </div>
          </div>

          <div class="origin-actions">
            <div class="origin-actions-title">{{ $t('details.actions_title') }}</div>
            <div class="origin-actions-grid">
              <div class="origin-action-card">
                <div class="action-info">
                  <span class="action-title">{{ $t('details.bid_price_title') }}</span>
                  <span class="action-cta">{{ isBuyer ?  $t('details.price_buyer_note') : $t('details.price_note') }}</span>
                </div>
                <button class="dock-btn dock-order" @click="ensureAuth('bid') ? (showBidModal = true) : null">
                  <i class="fa-solid fa-gavel"></i>
                  <span class="action-title">{{ $t('details.bid_price') }}</span>
                </button>

              </div>
              <div class="origin-action-card">
                <div class="action-info">
                  <span class="action-title">{{ $t('details.price_inquiry_title') }}</span>
                  <span class="action-cta">{{ $t('details.inquiry_cta') }}</span>
                </div>
                <button class="dock-btn dock-order" @click="ensureAuth('priceInquiry') ? (showPriceInquiryModal = true) : null" :disabled="daysPassed < 7">
                  <i class="fa-solid fa-tag"></i>
                  <span class="action-title">{{ $t('details.price_inquiry') }}</span>
                </button>
              </div>
              <div class="origin-action-card">
                <div class="action-info">
                  <span class="action-title">{{ $t('details.broadcast_ord_title') }}</span>
                  <span class="action-cta">{{ $t('details.broadcast_cta') }}</span>
                </div>
                <button class="dock-btn dock-order" @click="goToBroadcastOrd">
                  <i class="fa-solid fa-bullhorn"></i>
                  <span class="action-title">{{ $t('details.broadcast_ord') }}</span>
                </button>
              </div>
              <div class="origin-action-card">
                <div class="action-info">
                  <span class="action-title">{{ $t('details.support_chat_title') }}</span>
                  <span class="action-cta">{{ $t('details.chat_cta') }}</span>
                </div>
                <button class="dock-btn dock-order" @click="openSupportChat">
                  <i class="fa-solid fa-comment-dots"></i>
                  <span class="action-title">{{ $t('details.support_chat') }}</span>
                </button>
              </div>
              <div class="origin-action-card" v-if="isBuyer">
                <div class="action-info">
                  <span class="action-title">{{ $t('details.supply_product_title') }}</span>
                  <span class="action-cta">{{ $t('details.supply_cta') }}</span>
                </div>
                <button class="dock-btn dock-order" @click="ensureAuth('supply') ? (showSupplyRequestModal = true) : null">
                  <i class="fa-solid fa-handshake"></i>
                  <span class="action-title">{{ $t('details.supply_product') }}</span>
                </button>
              </div>
              <div class="origin-action-card">
                <div class="action-info">
                  <span class="action-title">{{ $t('details.contact_support_title') }}</span>
                  <span class="action-cta">{{ $t('details.contact_cta') }}</span>
                </div>
                <button class="dock-btn dock-order" @click="showContactModal = true">
                  <i class="fa-solid fa-phone"></i>
                  <span class="action-title">{{ $t('details.contact_support') }}</span>
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
    <HowItWorks v-if="!isBuyer" @contact="ensureAuth('buyNumber') ? (showBuyNumberModal = true) : null" @order="goToOrderPage()" />
    <section class="services-section">
      <div class="services-header">
        <div class="services-ornament">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.2"/>
            <path d="M12 6v12M6 12h12" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
          </svg>
        </div>
        <h3 class="services-title">{{ $t('details.services_title') }}</h3>
        <p class="services-subtitle">{{ $t('details.services_subtitle') }}</p>
      </div>

      <div class="services-grid">
        <div class="service-card">
          <div class="card-icon-wrap">
            <i class="fa-solid fa-truck-fast"></i>
          </div>
          <h4 class="card-title">{{ $t('details.service_transport_title') }}</h4>
          <p class="card-desc">{{ $t('details.service_transport_desc') }}</p>
        </div>

        <div class="service-card">
          <div class="card-icon-wrap">
            <i class="fa-solid fa-clipboard-check"></i>
          </div>
          <h4 class="card-title">{{ $t('details.service_inspection_title') }}</h4>
          <p class="card-desc">{{ $t('details.service_inspection_desc') }}</p>
        </div>

        <div class="service-card">
          <div class="card-icon-wrap">
            <i class="fa-solid fa-coins"></i>
          </div>
          <h4 class="card-title">{{ $t('details.service_finance_title') }}</h4>
          <p class="card-desc">{{ $t('details.service_finance_desc') }}</p>
        </div>

        <div class="service-card">
          <div class="card-icon-wrap">
            <i class="fa-solid fa-file-signature"></i>
          </div>
          <h4 class="card-title">{{ $t('details.service_legal_title') }}</h4>
          <p class="card-desc">{{ $t('details.service_legal_desc') }}</p>
        </div>
      </div>

      <div class="services-cta">
        <button class="dock-btn dock-order" @click="navigateTo(localePath('/about-services'))">
          <span>{{ $t('details.services_cta') }}</span>
          <i class="fa-solid fa-arrow-left"></i>
        </button>
      </div>
    </section>
    <!-- <HowItWorksBuyer @contact="ensureAuth('buyNumber') ? (showBuyNumberModal = true) : null" @order="goToOrderPage()" v-else /> -->


    <section class="cta-banner">
      <div class="cta-inner">
        <div class="cta-text">
          <p>{{ $t('details.cta_text_1') }} <strong>{{ $t('details.cta_text_strong') }}</strong> {{ $t('details.cta_text_2') }}</p>
        </div>
        <button class="cta-btn" @click="navigateTo(localePath('/add-ord'))">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
            <path d="M9 5a2 2 0 012-2h2a2 2 0 012 2v0a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            <path d="M9 14l2 2 4-4"/>
          </svg>
          <span>{{ $t('details.cta_btn') }}</span>
        </button>
      </div>
    </section>
    <section class="similar" v-if="similarProducts && similarProducts.length">
        <ProductList
          :products="similarProducts"
          :title="isBuyer ? $t('details.buyer_similar') : $t('details.similar')"
          icon="fa-solid fa-calendar-days"
          col-class="col-4"
          :badge-text="$t('home.badge_special')"
          :view-all-link="lastEndCat.value ? `/cat/${lastEndCat.value}/${lastEndCat.label}` : undefined"
        />
    </section>

    <section class="content-note reveal" v-if="contentTemplateText" :style="{ '--reveal-delay': '120ms' }">
      <div class="note-frame">
        <div class="note-veil" aria-hidden="true"></div>
        <div class="note-corner note-corner-tr" aria-hidden="true"></div>
        <div class="note-corner note-corner-bl" aria-hidden="true"></div>
        <div class="note-header">
          <span class="note-eyebrow">
            <i class="fa-solid fa-seedling"></i>
            {{ $t('details.note_eyebrow') }}
          </span>
          <button class="note-shuffle" @click="shuffleTemplate" :aria-label="$t('details.note_shuffle_aria')">
            <i class="fa-solid fa-rotate"></i>
            <span>{{ $t('details.note_shuffle') }}</span>
          </button>
        </div>
        <p class="note-text" v-html="contentTemplateText" @click="onNoteClick"></p>
        <div class="note-footer">
          <span class="note-footmark">
            <i class="fa-solid fa-leaf"></i>
            {{ $t('details.note_footmark') }}
          </span>
        </div>
      </div>
    </section>

<section class="faq-section reveal" v-if="faqList.length" :style="{ '--reveal-delay': '140ms' }">
      <div class="faq-header">
        <span class="faq-eyebrow">
          <i class="fa-solid fa-circle-question"></i>
          {{ isBuyer ? $t('details.faq_buyer_eyebrow') : $t('details.faq_eyebrow') }}
        </span>
        <h3 class="faq-title">
          {{ isBuyer ? $t('details.faq_buyer_title') : $t('details.faq_title') }}
        </h3>
      </div>
      <div class="faq-list">
        <div
          v-for="item in faqList"
          :key="item.id"
          class="faq-item"
          :class="{ 'is-open': openFaqId === item.id }"
        >
          <button class="faq-question" @click="toggleFaq(item.id)" :aria-expanded="openFaqId === item.id">
            <span class="faq-q-mark">
              <i class="fa-solid fa-question"></i>
            </span>
            <span class="faq-q-text" v-html="item.q"></span>
            <span class="faq-chevron">
              <i class="fa-solid fa-chevron-down"></i>
            </span>
          </button>
          <div class="faq-answer" :style="{ maxHeight: openFaqId === item.id ? '500px' : '0px' }">
            <div class="faq-answer-inner" v-html="item.a" @click="onNoteClick"></div>
          </div>
        </div>
      </div>
    </section>

    <section class="seo-article reveal" v-if="article" :style="{ '--reveal-delay': '160ms' }">
      <div class="article-inner">
        <h2 class="article-title">{{ article.title }}</h2>
        <div class="article-body" v-html="article.content"></div>
        <meta itemprop="keywords" :content="article.keywords?.join(', ')" />
      </div>
    </section>

    <div class="bottom-spacer"></div>

    <nav v-if="False" class="action-dock">
      <div class="dock-inner">
        <div class="dock-price">
          <span class="dock-amount">{{ toFa(formatNumber(product.price?.price || 0)) }}</span>
          <span class="dock-currency">{{ $t('details.currency') }}</span>
        </div>
        <div class="dock-actions">

          <button class="topbar-pill" :class="{ 'is-fav': isFavorited }" @click="toggleFavorite" :aria-label="$t('details.favorite')">
            <i class="fa-solid fa-heart"></i>
          </button>
          <button class="dock-btn dock-chat" @click="openChat">
            <i class="fa-solid fa-comment-dots"></i>
            <span>{{ $t('details.chat') }}</span>
          </button>
          <button class="dock-btn dock-order" @click="goToOrderPage" v-if="product.ad_type === 'seller'">
            <i class="fa-solid fa-cart-shopping"></i>
            <span>{{ $t('details.order') }}</span>
          </button>
        </div>
      </div>
    </nav>

    <Teleport to="body">
      <transition name="fade">
        <ChatBox v-if="showChat" :product="product" @close="closeChat" />
      </transition>
      <transition name="fade">
        <BidModal v-if="showBidModal" :product="product" :userTypeText="userType" :productUserId="product?.userId" @close="showBidModal = false" />
      </transition>
      <transition name="fade">
        <ContactModal v-if="showContactModal" :product="product" @close="showContactModal = false" />
      </transition>
      <transition name="fade">
        <PriceInquiryModal v-if="showPriceInquiryModal" :product="product" @close="showPriceInquiryModal = false" />
      </transition>
      <transition name="fade">
        <SupplyRequestModal v-if="showSupplyRequestModal" :product="product" @close="showSupplyRequestModal = false" />
      </transition>
      <transition name="fade">
        <BuyNumberModal v-if="showBuyNumberModal" :product="product" :userTypeText="userType" @close="showBuyNumberModal = false" />
      </transition>
      <transition name="fade">
        <LimitsModal v-if="showLimitsModal" :message="$t('details.insufficient_credit')" @close="showLimitsModal = false" />
      </transition>
      <transition name="fade">
        <ReportAdModal v-if="showReportModal" :product="product" @close="showReportModal = false" />
      </transition>
      <transition name="fade">
        <OutOfDateOrderModal
          v-if="showOutOfDateModal"
          :product="outOfDateModalProduct"
          :title="$t('details.out_of_date_title')"
          :message="$t('details.out_of_date_message')"
          @close="showOutOfDateModal = false"
          @confirm="handleOutOfDateConfirm"
          @price-inquiry="handleOutOfDatePriceInquiry"
        />
      </transition>
      <transition name="share-sheet">
        <div v-if="showShareModal" class="share-overlay" @click.self="showShareModal = false">
          <div class="share-sheet">
            <div class="share-header">
              <span class="share-title">{{ $t('share.title') }}</span>
              <button class="share-close" @click="showShareModal = false" :aria-label="$t('details.close')">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div class="share-actions">
              <button class="share-action" @click="shareOnWhatsApp">
                <span class="share-icon share-icon-whatsapp">
                  <i class="fa-brands fa-whatsapp"></i>
                </span>
                <span class="share-label">{{ $t('share.whatsapp') }}</span>
              </button>
              <button class="share-action" @click="shareOnTelegram">
                <span class="share-icon share-icon-telegram">
                  <i class="fa-brands fa-telegram"></i>
                </span>
                <span class="share-label">{{ $t('share.telegram') }}</span>
              </button>
              <button class="share-action" @click="copyShareLink">
                <span class="share-icon share-icon-copy">
                  <i class="fa-solid" :class="showCopyFeedback ? 'fa-check' : 'fa-link'"></i>
                </span>
                <span class="share-label">{{ showCopyFeedback ? $t('share.copied') : $t('share.copy_link') }}</span>
              </button>
            </div>
          </div>
        </div>
      </transition>
      <transition name="lightbox">
        <div v-if="showLightbox" class="lightbox-overlay" @click.self="closeLightbox">
          <button class="lightbox-close" @click="closeLightbox" :aria-label="$t('details.close')">
            <i class="fa-solid fa-xmark"></i>
          </button>
          <div class="lightbox-toolbar">
            <button class="lb-btn" @click="zoomOut" :disabled="zoomLevel <= 0.5" :aria-label="$t('details.zoom_out')">
              <i class="fa-solid fa-minus"></i>
            </button>
            <span class="lb-zoom-level">{{ Math.round(zoomLevel * 100) }}%</span>
            <button class="lb-btn" @click="zoomIn" :aria-label="$t('details.zoom_in')">
              <i class="fa-solid fa-plus"></i>
            </button>
            <button class="lb-btn" @click="resetZoom" :aria-label="$t('details.reset_zoom')">
              <i class="fa-solid fa-expand"></i>
            </button>
            <span class="lb-sep"></span>
          </div>
          <template v-if="isVideoInLightbox">
            <video
              :src="heroSrc"
              class="lightbox-media"
              controls
              autoplay
              playsinline
            ></video>
          </template>
          <div v-else class="lightbox-image-wrap" ref="lightboxWrapRef">
            <img
              :src="lightboxSrc"
              :alt="displayTitle"
              class="lightbox-image"
              :style="{ transform: `scale(${zoomLevel})`, cursor: isPanning ? 'grabbing' : 'grab' }"
              @mousedown="startPan"
              @mousemove="pan"
              @mouseup="endPan"
              @mouseleave="endPan"
              @touchstart.prevent="startPinch"
              @touchmove.prevent="pinch"
              @touchend="endPinch"
              draggable="false"
            />
            <div class="lightbox-frame-overlay" aria-hidden="true">
              <img src="/img/sarvban_logo_2.png" alt="" class="lb-frame-logo" />
            </div>
          </div>
          <template v-if="galleryImages.length > 1">
            <button class="lightbox-nav lightbox-next" @click.stop="prevImage">
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button class="lightbox-nav lightbox-prev" @click.stop="nextImage">
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <div class="lightbox-counter">
              {{ toFa(currentIndex + 1) }} / {{ toFa(galleryImages.length) }}
            </div>
          </template>
          <div class="lightbox-thumbs">
            <button
              v-for="(img, idx) in galleryImages"
              :key="idx"
              class="lb-thumb"
              :class="{ 'is-active': idx === currentIndex }"
              @click="currentIndex = idx"
            >
              <img :src="lbSrc(imgSrc(img, true))" :alt="displayTitle + ' ' + (idx + 1)" />
              <span v-if="isVideoFile(img)" class="lb-thumb-video">
                <i class="fa-solid fa-play"></i>
              </span>
            </button>
          </div>
        </div>
      </transition>
    </Teleport>

  </div>

  <div class="harvest-loading" v-else-if="loading">
    <div class="loading-orb">
      <i class="fa-solid fa-leaf"></i>
    </div>
    <p>{{ $t('details.loading_product') }}</p>
  </div>

  <div class="harvest-error" v-else-if="error">
    <div class="error-orb">
      <i class="fa-solid fa-triangle-exclamation"></i>
    </div>
    <h4>{{ $t('details.load_error') }}</h4>
    <p>{{ error }}</p>
    <button class="retry-btn" @click="refreshData">
      <i class="fa-solid fa-rotate-right"></i>
      {{ $t('details.retry') }}
    </button>
  </div>
  <SupportChat ref="supportChatRef" :key="id" :product="product" :show-prompt="true"/>
  <Footer></Footer>
</template>

<script setup>
const baseURL = useRuntimeConfig().app.baseURL
const siteUrl = useRuntimeConfig().public.baseUrl
const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
import { useCurrencyConversion } from '@/composables/useCurrencyConversion'
const localePath = useLocalePath()
import OutOfDateOrderModal from '@/components/OutOfDateOrderModal.vue'
const id = computed(() => route.params.id || '')
const idSource = toRef(route.params, 'id')
const showChat = ref(false)
const supportChatRef = ref(null)
const showShareModal = ref(false)
const showBidModal = ref(false)
const showContactModal = ref(false)
const showPriceInquiryModal = ref(false)
const showSupplyRequestModal = ref(false)
const showBuyNumberModal = ref(false)
const showLimitsModal = ref(false)
const showReportModal = ref(false)
const showFullDesc = ref(false)
const isFavorited = ref(false)
const showCopyFeedback = ref(false)
const showOutOfDateModal = ref(false)
const currentIndex = ref(0)
const showLightbox = ref(false)
const zoomLevel = ref(1)
const isPanning = ref(false)
const panStart = ref({ x: 0, y: 0 })
const panOffset = ref({ x: 0, y: 0 })
const isPlaying = ref(false)
const heroVideoRef = ref(null)
const lightboxWrapRef = ref(null)

// Pinch state
const pinchDist = ref(0)
const pinchZoom = ref(1)

const similarProducts = ref([])

// Edge nav visibility on mobile
const activeEdge = ref(null) // 'left' | 'right' | null

// Navigation between similar products (album-style)
const currentProductIndex = computed(() => {
  if (!similarProducts.value || !product.value) return -1
  return similarProducts.value.findIndex(p => p.invoiceNum === product.value.invoiceNum)
})

const prevProduct = computed(() => {
  const idx = currentProductIndex.value
  if (idx === -1) {
    // current product not in the similar list – offer first item as a starting point
    return similarProducts.value[0] ?? null
  }
  if (idx <= 0) return null
  return similarProducts.value[idx - 1]
})

const nextProduct = computed(() => {
  const idx = currentProductIndex.value
  if (idx === -1) {
    // current product not in the similar list – offer the second item so prev and next differ
    return similarProducts.value[1] ?? null
  }
  if (idx < 0 || idx >= similarProducts.value.length - 1) return null
  return similarProducts.value[idx + 1]
})

const goToProduct = (productId) => {
  if (!productId) return
  navigateTo(localePath(`/details/${productId}`))
}

const article = ref(null)
const articleLoading = ref(false)
const loading = ref(true)
const error = ref(null)
const contentTemplates = [
    {
        id: 1,
        text: t('details.content_template_1')
    },
    {
        id: 2,
        text: t('details.content_template_2')
    },
    {
        id: 3,
        text: t('details.content_template_3')
    },
    {
        id: 4,
        text: t('details.content_template_4')
    },
    {
        id: 5,
        text: t('details.content_template_5')
    },
    {
        id: 6,
        text: t('details.content_template_6')
    }
];
const faqTemplates = [
    {
        id: 1,
        q: t('details.faq_1_q'),
        a: t('details.faq_1_a')
    },
    {
        id: 2,
        q: t('details.faq_2_q'),
        a: t('details.faq_2_a')
    },
    {
        id: 3,
        q: t('details.faq_3_q'),
        a: t('details.faq_3_a')
    },
    {
        id: 4,
        q: t('details.faq_4_q'),
        a: t('details.faq_4_a')
    },
    {
        id: 5,
        q: t('details.faq_5_q'),
        a: t('details.faq_5_a')
    },
    {
        id: 6,
        q: t('details.faq_6_q'),
        a: t('details.faq_6_a')
    },
    {
        id: 7,
        q: t('details.faq_7_q'),
        a: t('details.faq_7_a')
    },
    {
        id: 8,
        q: t('details.faq_8_q'),
        a: t('details.faq_8_a')
    },
    {
        id: 9,
        q: t('details.faq_9_q'),
        a: t('details.faq_9_a')
    },
];

const faqTemplatesBuyer = [
    {
        id: 1,
        q: t('details.faq_buyer_1_q'),
        a: t('details.faq_buyer_1_a')
    },
    {
        id: 2,
        q: t('details.faq_buyer_2_q'),
        a: t('details.faq_buyer_2_a')
    },
    {
        id: 3,
        q: t('details.faq_buyer_3_q'),
        a: t('details.faq_buyer_3_a')
    },
    {
        id: 4,
        q: t('details.faq_buyer_4_q'),
        a: t('details.faq_buyer_4_a')
    },
    {
        id: 5,
        q: t('details.faq_buyer_5_q'),
        a: t('details.faq_buyer_5_a')
    },
];

const openFaqId = ref(1)
definePageMeta({
  layout: 'default'
})

// Fetch product data
const { data: product, refresh } = await useFetch(`/market/api/products/${id.value}`, {
  key: `product-${id.value}`,
  default: () => null,
  onResponseError: () => {
    error.value = t('details.product_not_found')
  }
})

// Gallery images
const galleryImages = computed(() => {
  const imgs = []
  const process = (img) => {
    if (!img) return
    if (typeof img === 'string') imgs.push(img)
    else if (img.url) imgs.push(img.url)
    else if (img.path) imgs.push(img.path)
  }
  if (product.value?.images && Array.isArray(product.value.images)) {
    product.value.images.forEach(process)
  }
  if (imgs.length === 0 && product.value?.image) {
    if (Array.isArray(product.value.image)) {
      product.value.image.forEach(process)
    } else {
      process(product.value.image)
    }
  }
  return imgs.length > 0 ? imgs : []
})

const currentImage = computed(() => galleryImages.value[currentIndex.value] || galleryImages.value[0])

const isVideoFile = (img) => {
  if (!img) return false
  const src = typeof img === 'string' ? img : (img.url || img.path || '')
  return src.endsWith('.mp4') || src.endsWith('.mov') || src.endsWith('.webm')
}

const isVideo = computed(() => isVideoFile(currentImage.value))

const isVideoInLightbox = computed(() => isVideoFile(galleryImages.value[currentIndex.value] || galleryImages.value[0]))

const videoPoster = computed(() => {
  if (!isVideo.value) return ''
  const idx = currentIndex.value
  const prevIdx = idx > 0 ? idx - 1 : (galleryImages.value.length > 1 ? galleryImages.value.length - 1 : -1)
  if (prevIdx >= 0) return imgSrc(galleryImages.value[prevIdx])
  return ''
})

const imgSrc = (img, thumb = false) => {
  if (!img) return `/img/products/default.jpg`
  const src = typeof img === 'string' ? img : (img.url || img.path || '')
  if (!src) return `/img/products/default.jpg`
  if (src.startsWith('/') || src.startsWith('http')) return src
  if (thumb && isVideoFile(img)) return `/img/default-video.jpg`
  return `https://sarvban.com/uploads/market/${product.value?.userId}/${src}`
}

const loadPrevProductImage = computed(() => `${siteUrl}/uploads/market/${prevProduct.value.userId}/${prevProduct.value.image}`)
const loadNextProductImage = computed(() => `${siteUrl}/uploads/market/${nextProduct.value.userId}/${nextProduct.value.image}`)

const heroSrc = computed(() => imgSrc(currentImage.value))

const currentHeroSrc = ref(heroSrc.value)

watch(heroSrc, (src) => {
  currentHeroSrc.value = src
})

// ===== SEO helpers =====
const canonicalUrl = computed(() => {
  const localePrefix = locale.value === 'fa' ? '' : locale.value + '/'
  return `${siteUrl}${baseURL}${localePrefix}details/${id.value}`
})
const cleanDesc = computed(() => {
  const raw = product.value?.description || ''
  return raw.length > 155 ? raw.slice(0, 152) + '…' : raw
})
const ogImageUrl = computed(() => {
  const first = galleryImages.value[0]
  if (!first) {
    return `${siteUrl}${baseURL}img/products/default.jpg`
  }
  const src = typeof first === 'string' ? first : (first.url || first.path || '')
  if (!src) {
    return `${siteUrl}${baseURL}img/products/default.jpg`
  }
  if (src.startsWith('http')) return src
  if (src.startsWith('/')) return `${siteUrl}${src}`
  return `${siteUrl}/uploads/market/${product.value?.userId || ''}/${src}`
})

const isOutOfSeason = computed(() => {
  if (!product.value) return false
  const adType = product.value.adType || product.value.ad_type || ''
  return adType.toLowerCase().includes('outofseasion')
})

const displayTitle = computed(() => {
  if (!product.value || !product.value.title) return ''
  return isOutOfSeason.value ? `پیش ${product.value.title}` : product.value.title
})
const lastEndCat = computed(() => {
  if (!product.value || !Array.isArray(product.value.cat_path) || !product.value.cat_path.length) return null
  return [...product.value.cat_path].reverse().find(c => c?.end === true) || null
})
useSeoMeta({
  title: displayTitle,
  description: cleanDesc,
  ogTitle: displayTitle,
  ogDescription: cleanDesc,
  ogType: 'product',
  ogImage: ogImageUrl,
  ogUrl: canonicalUrl,
  ogSiteName: 'سروبان',
  twitterCard: 'summary_large_image',
  twitterTitle: displayTitle,
  twitterDescription: cleanDesc,
  twitterImage: ogImageUrl
})

useHead({
  link: [
    { rel: 'canonical', href: canonicalUrl }
  ],
  htmlAttrs: {
    lang: locale.value === 'ar' ? 'ar' : locale.value === 'ru' ? 'ru' : locale.value === 'tr' ? 'tr' : locale.value === 'en' ? 'en' : 'fa',
    dir: locale.value === 'en' || locale.value === 'ru' || locale.value === 'tr' ? 'ltr' : 'rtl'
  }
})

const lbSrc = (src) => {
  if (!src) return `${baseURL}/img/products/default.jpg`
  if (src.startsWith('/') && !src.startsWith('//')) return `${siteUrl}${src}`
  return src
}

const lightboxSrc = computed(() => lbSrc(currentHeroSrc.value))

const onHeroError = () => {
  currentHeroSrc.value = `${baseURL}/img/products/default.jpg`
}

// Lightbox methods
const openLightbox = () => {
  if (isVideo.value) return
  showLightbox.value = true
  zoomLevel.value = 1
  panOffset.value = { x: 0, y: 0 }
}

const closeLightbox = () => {
  showLightbox.value = false
  zoomLevel.value = 1
  panOffset.value = { x: 0, y: 0 }
}

const zoomIn = () => {
  zoomLevel.value = Math.min(zoomLevel.value + 0.5, 5)
}

const zoomOut = () => {
  zoomLevel.value = Math.max(zoomLevel.value - 0.5, 0.5)
}

const resetZoom = () => {
  zoomLevel.value = 1
  panOffset.value = { x: 0, y: 0 }
}

const downloadImage = () => {
  const link = document.createElement('a')
  link.href = currentHeroSrc.value
  link.download = product.value?.title || 'image'
  link.click()
}

// Pan
const startPan = (e) => {
  if (zoomLevel.value <= 1) return
  isPanning.value = true
  panStart.value = { x: e.clientX - panOffset.value.x, y: e.clientY - panOffset.value.y }
}

const pan = (e) => {
  if (!isPanning.value) return
  panOffset.value = { x: e.clientX - panStart.value.x, y: e.clientY - panStart.value.y }
}

const endPan = () => {
  isPanning.value = false
}

// Pinch zoom
let initialPinchDist = 0
let initialPinchZoom = 1

const startPinch = (e) => {
  if (e.touches.length === 2) {
    initialPinchDist = Math.hypot(
      e.touches[0].clientX - e.touches[1].clientX,
      e.touches[0].clientY - e.touches[1].clientY
    )
    initialPinchZoom = zoomLevel.value
  }
}

const pinch = (e) => {
  if (e.touches.length === 2) {
    const dist = Math.hypot(
      e.touches[0].clientX - e.touches[1].clientX,
      e.touches[0].clientY - e.touches[1].clientY
    )
    const scale = dist / initialPinchDist
    zoomLevel.value = Math.min(Math.max(initialPinchZoom * scale, 0.5), 5)
  }
}

const endPinch = () => {
  initialPinchDist = 0
}

// Video play/pause
const togglePlay = () => {
  if (!heroVideoRef.value) return
  if (isPlaying.value) {
    heroVideoRef.value.pause()
  } else {
    heroVideoRef.value.play()
  }
  isPlaying.value = !isPlaying.value
}

watch(currentIndex, () => {
  isPlaying.value = false
})

const cleanTitle = computed(() => {
  if (!product.value?.title) return ''
  const title = product.value.title.replace('خرید', '').replace('پیش', '').replace('فروش', '')
  return isOutOfSeason.value ? `${title}` : title
})

const productCat = computed(() => {
  if (!product.value?.cat_path || !product.value?.cat_path.length) return {}
  return product.value.cat_path[product.value.cat_path.length - 1]
})

const daysPassed = computed(() => {
  if (!product.value?.timestamp) return 0
  const start = product.value.timestamp * 1000 
  const end = Date.now()
  const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24))
  product.value.daysPassed=diffDays
  return diffDays
})

// Methods
const formatNumber = (num) => {
  return new Intl.NumberFormat('fa-IR').format(num)
}

// Convert any Latin digits in a value to Persian (handles strings, numbers, dates)
const toFa = (val) => {
  if (val === null || val === undefined) return ''
  return String(val).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d])
}

const updateLabel = computed(() => {
  const days = daysPassed.value
  if (days === 0) return t('details.update_today')
  if (days <= 10) return `${t('details.update_prefix')}${toFa(days)}${t('details.update_suffix')}`
  return t('details.inquire_stock')
})




const isBuyer = computed(() => {
  const adType = product.value?.adType || product.value?.ad_type || ''
  return adType.toLowerCase().includes('buyer')
})

const adTypeLabel = computed(() => {
  if (!product.value?.ad_type) return t('details.product_label')
  if (product.value.ad_type.includes('seller') || product.value.ad_type.includes('outofseasion')) return t('details.for_sale')
  if (product.value.ad_type.includes('buyer')) return t('details.buy_request')
  return t('details.product_label')
})

const parsedDescription = computed(() => {
  if (!product.value?.description) return ''
  return product.value.description.replace(/\n/g, '<br/>')
})

const randomTemplateId = ref(null)

const noteSearch = (term) => {
  return `/market/search/${encodeURIComponent(term || '')}`
}

const fillTemplate = (template) => {
  if (!template) return ''
  const location = product.value?.address?.city || product.value?.address?.ostan || ''
  const productName = cleanTitle.value || product.value?.title || ''
  const farmerName = product.value?.username || ''
  const productType = lastEndCat.value?.label || ''
  return template.text
    .replaceAll('${product_name}', `<a class="note-inline note-inline-product" href="${noteSearch(productName)}">${productName}</a>`)
    .replaceAll('${location}', `<a class="note-inline note-inline-location" href="${noteSearch(location)}">${location}</a>`)
    .replaceAll('${farmer_name}', `<button type="button" class="note-inline note-inline-call" data-note-call>${farmerName}</button>`)
    .replaceAll('${product_code}', toFa(product.value?.invoiceNum || ''))
    .replaceAll('${product_type}', `<a class="note-inline note-inline-type" href="${noteSearch(productType)}">${productType}</a>`)
}

const fillFaqText = (text) => {
  if (!text) return ''
  const location = product.value?.address?.city || product.value?.address?.ostan || ''
  const productName = cleanTitle.value || product.value?.title || ''
  const farmerName = product.value?.username || ''
  const productType = lastEndCat.value?.label || ''
  const servicesLinkText = t('details.services_cta')
  return text
    .replaceAll('${product_name}', `<a class="note-inline note-inline-product" href="${noteSearch(productName)}">${productName}</a>`)
    .replaceAll('${location}', `<a class="note-inline note-inline-location" href="${noteSearch(location)}">${location}</a>`)
    .replaceAll('${farmer_name}', `<button type="button" class="note-inline note-inline-call" data-note-call>${farmerName}</button>`)
    .replaceAll('${product_code}', toFa(product.value?.invoiceNum || ''))
    .replaceAll('${product_type}', `<a class="note-inline note-inline-type" href="${noteSearch(productType)}">${productType}</a>`)
    .replaceAll('${services_link}', `<a class="note-inline note-inline-product" href="/about-services">${servicesLinkText}</a>`)
}

const fillFaqTextBuyer = (text) => {
  if (!text) return ''
  const location = product.value?.address?.city || product.value?.address?.ostan || ''
  const productName = cleanTitle.value || product.value?.title || ''
  const farmerName = product.value?.username || ''
  const productType = lastEndCat.value?.label || ''
  return text
    .replaceAll('${product_name}', `<a class="note-inline note-inline-product" href="${noteSearch(productName)}">${productName}</a>`)
    .replaceAll('${location}', `<a class="note-inline note-inline-location" href="${noteSearch(location)}">${location}</a>`)
    .replaceAll('${farmer_name}', `<button type="button" class="note-inline note-inline-call" data-note-call>${farmerName}</button>`)
    .replaceAll('${product_code}', toFa(product.value?.invoiceNum || ''))
    .replaceAll('${product_type}', `<a class="note-inline note-inline-type" href="${noteSearch(productType)}">${productType}</a>`)
}

const faqList = computed(() => {
  return isBuyer.value ? faqTemplatesBuyer.map(item => ({
    id: item.id,
    q: fillFaqTextBuyer(item.q),
    a: fillFaqTextBuyer(item.a)
  })) : faqTemplates.map(item => ({
    id: item.id,
    q: fillFaqText(item.q),
    a: fillFaqText(item.a)
  }))
})

const stripHtml = (html) => {
  if (!html) return ''
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim()
}



const detailSchema = computed(() => {
  const productData = product.value
  if (!productData) return []
  const availability = productData.market
    ? 'OutOfStock'
    : productData.status === false
      ? 'OutOfStock'
      : 'InStock'

  const nodes = [
    defineProduct({
      name: displayTitle.value || '',
      description: cleanDesc.value || '',
      image: ogImageUrl.value || '',
      offers: {
        price: productData.price?.price || 0,
        priceCurrency: 'IRR',
        availability,
        url: canonicalUrl.value
      }
    }),
    defineBreadcrumb({
      itemListElement: [
        { name: 'سروبان', item: `${siteUrl}${baseURL}` },
        ...(lastEndCat.value
          ? [
              {
                name: lastEndCat.value.label,
                item: `${siteUrl}${baseURL}${locale.value === 'fa' ? '/' : locale.value + '/'}cat/${lastEndCat.value.value}/${lastEndCat.value.label}`
              }
            ]
          : []),
        { name: displayTitle.value || '', item: canonicalUrl.value }
      ]
    })
  ]

  const faqData = Array.isArray(faqList.value) ? faqList.value : []
  if (faqData.length) {
    nodes.push({
      '@type': 'FAQPage',
      mainEntity: faqData.map((item) =>
        defineQuestion({
          name: stripHtml(item.q),
          acceptedAnswer: stripHtml(item.a)
        })
      )
    })
  }

  return nodes
})

useSchemaOrg(product.value ? detailSchema : [])

const toggleFaq = (id) => {
  openFaqId.value = openFaqId.value === id ? null : id
}

const pickRandomTemplate = (avoidCurrent = true) => {
  if (!contentTemplates.length) return
  let candidates = contentTemplates
  if (avoidCurrent && randomTemplateId.value) {
    candidates = contentTemplates.filter(t => t.id !== randomTemplateId.value)
  }
  if (!candidates.length) candidates = contentTemplates
  randomTemplateId.value = candidates[Math.floor(Math.random() * candidates.length)].id
}

const contentTemplateText = computed(() => {
  if (!contentTemplates.length) return ''
  const template = contentTemplates.find(t => t.id === randomTemplateId.value)
  return fillTemplate(template || contentTemplates[0])
})

const shuffleTemplate = () => pickRandomTemplate(true)

const onNoteClick = (e) => {
  const callBtn = e.target.closest('[data-note-call]')
  if (!callBtn) return
  e.preventDefault()
  if (ensureAuth('buyNumber')) showBuyNumberModal.value = true
}

const statusText = computed(() => {
  if (!product.value) return ''
  if (product.value.market) return t('details.sold_out')
  if (!product.value.status) return t('details.inactive')

  const days = daysPassed.value
  if (days <= 5 && !isBuyer) return t('details.fresh_available')
  if (days <= 5) return t('details.active')
  if (days > 5 && days < 10) return t('details.check_availability')
  if (days >= 10) return t('details.inquire')
  return ''
})

const statusClass = computed(() => {
  if (!product.value) return ''
  if (product.value.market) return 'st-sold'
  if (!product.value.status) return 'st-inactive'
  const days = daysPassed.value
  if (days <= 5) return 'st-active'
  if (days > 5 && days < 10) return 'st-pending'
  if (days >= 10) return 'st-inquiry'
  return ''
})

const userType = computed(() => {
  if (!product.value?.user?.usertype) return t('details.seller_type')
  const usertype = product.value.user.usertype
  if (isBuyer.value) return t('details.buyer_type')
  if (['200', '201', '202'].includes(usertype)) return t('details.farmer')
  if (['203', '205'].includes(usertype)) return t('details.local_supplier')
  if (['207', '206'].includes(usertype)) return t('details.gardener')
  if (['204'].includes(usertype)) return t('details.regional_supplier')
  if (['208', '212'].includes(usertype)) return t('details.sorting_storage')
  return t('details.seller_type')
})

const groupedInfo = computed(() => {
  const groups = new Map()
  product.value?.info?.forEach(spec => {
    if (!groups.has(spec.parent)) groups.set(spec.parent, [])
    groups.get(spec.parent).push(spec)
  })
  return Array.from(groups.entries())
})

const vahed = computed(() => {
  const item = product.value?.info?.find(i => i.parent === 'واحد')
  return item ? item.title : t('details.default_unit')
})

const outOfDateModalProduct = computed(() => {
  if (!product.value) return null
  return {
    title: product.value.title || '',
    daysPassed: daysPassed.value,
    price: product.value.price?.price || 0,
    count: product.value.price?.count || 0,
    unit: vahed.value
  }
})

// Exportable products check (info.value = 3358 for صادراتی)
const isExportable = computed(() => {
// console.log(product.value?.info)
  if (!product.value?.info || !Array.isArray(product.value.info)) return false
  return product.value.info.some(spec => spec.id === "3358" || spec.value === "3358" || spec.parent === 'صادراتی')
})

// Currency conversion rates
const API_BASE = 'https://sarvban.com/app/api/anar'
const usdRate = ref(null)
const iqdRate = ref(null)
const rubRate = ref(null)
const currencyLoading = ref(false)
const currencyError = ref(null)

const fetchCurrencyRates = async () => {
  // if (!isExportable.value) return
  currencyLoading.value = true
  currencyError.value = null
  try {
    // Fetch IQD rate (flag-iq for Iraq)
    const iqdResp = await fetch(`${API_BASE}/get-currency/?flag=flag-iq`)
    if (iqdResp.ok) {
      const iqdData = await iqdResp.json()
      if (iqdData.ok && iqdData.price) {
        iqdRate.value = iqdData.price
      }
    }    
    // Fetch IQD rate (flag-ru for Russia)
    const rubResp = await fetch(`${API_BASE}/get-currency/?flag=flag-ru`)
    if (rubResp.ok) {
      const rubData = await rubResp.json()
      if (rubData.ok && rubData.price) {
        rubRate.value = rubData.price
      }
    }
    // Fetch USD rate (flag-us)
    const usdResp = await fetch(`${API_BASE}/get-currency/?flag=flag-us`)
    if (usdResp.ok) {
      const usdData = await usdResp.json()
      if (usdData.ok && usdData.price) {
        usdRate.value = usdData.price
        // console.log(usdRate.value)
      }
    }
    
  } catch (e) {
    console.error('Error fetching currency rates:', e)
    currencyError.value = t('details.currency_fetch_error') || 'خطا در دریافت نرخ ارز'
  } finally {
    currencyLoading.value = false
  }
}

// Convert price to USD and IQD
const priceInUSD = computed(() => {
  if (!usdRate.value || !product.value?.price?.price) return null
  const price = Number(product.value.price.price)*10
  return (price / usdRate.value)
})

const priceInIQD = computed(() => {
  if (!iqdRate.value || !product.value?.price?.price) return null
  const price = Number(product.value.price.price)*10
  return Math.round(price / iqdRate.value)
})

const priceInRUB = computed(() => {
  if (!rubRate.value || !product.value?.price?.price) return null
  const price = Number(product.value.price.price)*10
  return Math.round(price / rubRate.value)
})

// Fetch currency rates when product loads and is exportable
watch(product, (newProduct) => {
  if (newProduct ) {
    fetchCurrencyRates()
  }
}, { immediate: true })
// const currency = useCurrencyConversion(product)

const prevImage = () => {
  currentIndex.value = currentIndex.value > 0 ? currentIndex.value - 1 : galleryImages.value.length - 1
}

const nextImage = () => {
  currentIndex.value = currentIndex.value < galleryImages.value.length - 1 ? currentIndex.value + 1 : 0
}

const toggleZoom = () => {
  // Hook for future lightbox
}

const toggleFavorite = () => {
  isFavorited.value = !isFavorited.value
}

const openShareModal = () => {
  showShareModal.value = true
}

const shareOnWhatsApp = () => {
  const url = `${window.location.origin}${baseURL}details/${id.value}`
  const text = product.value?.title || t('details.share_title')
  window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(url + ' - ' + text)}`, '_blank')
}

const shareOnTelegram = () => {
  const url = `${window.location.origin}${baseURL}details/${id.value}`
  const text = product.value?.title || t('details.share_title')
  window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank')
}

const copyShareLink = async () => {
  try {
    const url = `${window.location.origin}${baseURL}details/${id.value}`
    await navigator.clipboard.writeText(url)
    // Show brief copied feedback
    showCopyFeedback.value = true
    setTimeout(() => {
      showCopyFeedback.value = false
      showShareModal.value = false
    }, 1200)
  } catch (e) {
    console.error('Copy failed:', e)
  }
}

// Returns true when the user is authenticated; otherwise shows a confirm
// dialog, then sends them to /login with a `redirect` query pointing back
// to the current page (including which modal to re-open after sign-in).
const ensureAuth = (modalName) => {
  const auth = useAuth()
  if (auth.authenticated.value) return true

  const confirmed = window.confirm(t('details.login_required'))
  if (!confirmed) return false

  const returnTo = route.fullPath
  const redirectUrl = modalName
    ? `${returnTo}${returnTo.includes('?') ? '&' : '?'}openModal=${modalName}`
    : returnTo

  // Use window.location to bypass Nuxt auth middleware redirect loop
   navigateTo(localePath(`/login?redirect=${encodeURIComponent(redirectUrl)}`))

  return false
}

const goToOrderPage = () => {
  if (!ensureAuth('order')) return
  
  // Check if product is older than 30 days
  if (product.value && daysPassed.value > 30) {
    showOutOfDateModal.value = true
    return
  }
  
  navigateTo(localePath(`/order/${product.value?.invoiceNum}`))
}

const handleOutOfDateConfirm = () => {
  if (!product.value) return
  
  // Prepare data to pass to add-ord wizard
  const catPath = product.value.cat_path || []
  const lastEndCat = [...catPath].reverse().find(c => c?.end === true)
  const price = product.value.price?.price || 0
  const count = product.value.price?.count || 0
  const unit = product.value.info?.find(i => i.parent === 'واحد')?.title || ''
  
  // Build query parameters
  const queryParams = new URLSearchParams()
  if (lastEndCat?.value) queryParams.set('cat', lastEndCat.value)
  if (lastEndCat?.label) queryParams.set('catLabel', lastEndCat.label)
  if (catPath.length) queryParams.set('catPath', JSON.stringify(catPath))
  if (price) queryParams.set('price', String(price))
  if (count) queryParams.set('count', String(count))
  if (unit) queryParams.set('unit', unit)
  if (product.value.invoiceNum) queryParams.set('refId', String(product.value.invoiceNum))
  if (product.value.title) queryParams.set('refTitle', product.value.title)
  
  navigateTo(localePath(`/add-ord?${queryParams.toString()}`))
}

const handleOutOfDatePriceInquiry = () => {
  if (!ensureAuth('priceInquiry')) return
  showPriceInquiryModal.value = true
}

const openChat = () => {
  if (!ensureAuth('chat')) return
  showChat.value = true
}

const openSupportChat = () => {
  if (supportChatRef.value) {
    supportChatRef.value.open()
  }
}

const goToBroadcastOrd = () => {
  navigateTo(localePath('/my-sarvban/broadcast-ord'))
}

const closeChat = () => {
  showChat.value = false
}

const refreshData = () => {
  error.value = null
  loading.value = true
  refresh()
}

const fetchSimilarProducts = async () => {
  const productData = product.value
  if (!productData || !Array.isArray(productData.cat_path) || !productData.cat_path.length) return
  const last = lastEndCat.value
  const first = productData.cat_path[0]
  if (!last || !first) return
  try {
    const data = await $fetch('/market/api/products/similar', {
      method: 'POST',
      body: {
        parent: last.parent || '',
        post_id: productData.invoiceNum || '',
        main_cat: lastEndCat.value.value || '',
        ad_type: productData.ad_type || 'seller'
      }
    })
    similarProducts.value = data || []
  } catch (e) {
    console.error('Error fetching similar products:', e)
  }
}

const fetchArticle = async () => {
  if (!product.value?.title) return
  articleLoading.value = true
  try {
    const data = await $fetch('/market/api/article', {
      method: 'POST',
      body: {
        title: product.value.title
      }
    })
    article.value = data?.data || null
  } catch (e) {
    console.error('Error fetching article:', e)
    article.value = null
  } finally {
    articleLoading.value = false
  }
}

// Currency conversion composable

const handleKeydown = (e) => {
  if (!showLightbox.value) return
  if (e.key === 'Escape') closeLightbox()
  if (e.key === 'ArrowLeft') prevImage()
  if (e.key === 'ArrowRight') nextImage()
}

// Scroll reveal observer
onMounted(() => {
  loading.value = false
  fetchSimilarProducts()
  pickRandomTemplate()
  document.addEventListener('keydown', handleKeydown)

  // Open modal if redirected back from login
  const modalToOpen = route.query.openModal
  if (modalToOpen) {
    const modalMap = {
      bid: () => { showBidModal.value = true },
      priceInquiry: () => { showPriceInquiryModal.value = true },
      contact: () => { showContactModal.value = true },
      supply: () => { showSupplyRequestModal.value = true },
      buyNumber: () => { showBuyNumberModal.value = true },
      chat: () => { showChat.value = true },
      order: () => { goToOrderPage() },
    }
    if (modalMap[modalToOpen]) {
      modalMap[modalToOpen]()
    }
    // Clean up the query param without triggering navigation
    router.replace({ query: { ...route.query, openModal: undefined } })
  }

  if (process.client) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    )
    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el))
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

// Re-fetch product + similar-products whenever the route id changes (component reuse on navigateTo)
watch(idSource, async (newId, oldId) => {
  if (!newId || newId === oldId) return
  similarProducts.value = []
  article.value = null
  articleLoading.value = false
  currentIndex.value = 0
  await refresh()
})

watch(product, (current) => {
  if (current) {
    loading.value = false
    fetchSimilarProducts()
    fetchArticle()
  }
})
</script>

<style lang="scss" scoped>
@use "sass:color";

.harvest-detail {
  background: linear-gradient(136deg, #f8f6f3, #f8f1f0);
  padding-top: 50px;
  position: relative;
  color: $ink;
  min-height: 100vh;
  overflow: hidden;
  font-family: 'IRANYekan', 'Vazirmatn', sans-serif;
  direction: rtl;
  padding-bottom: 0;
}
.atmosphere-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.grain-overlay {
  position: absolute;
  inset: -50%;
  opacity: 0.35;
  background-image:
    radial-gradient(circle at 20% 30%, rgba(200, 134, 26, 0.04) 0%, transparent 40%),
    url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E");
  mix-blend-mode: multiply;
}

.blob-shape {
  position: absolute;
  width: 340px;
  height: 340px;
  opacity: 0.5;
  filter: blur(8px);

  &.blob-1 {
    color: rgba(232, 168, 56, 0.18);
    top: -80px;
    left: -120px;
    animation: blob-drift-1 22s ease-in-out infinite;
  }

  &.blob-2 {
    color: rgba(74, 140, 94, 0.16);
    bottom: 10%;
    right: -140px;
    width: 280px;
    height: 280px;
    animation: blob-drift-2 28s ease-in-out infinite;
  }
}

@keyframes blob-drift-1 {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50%      { transform: translate(40px, 60px) rotate(40deg); }
}
@keyframes blob-drift-2 {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50%      { transform: translate(-50px, -40px) rotate(-30deg); }
}

// All content sits above atmosphere
.detail-shell,
.harvest-topbar,
.action-dock {
  position: relative;
  z-index: 1;
}


.detail-shell {
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 16px;

  .detail-aside,
  .detail-main {
    display: block;
  }
}

@media (max-width : 480px) {
.detail-shell{
  padding: 0 5px;
}
}
@media (min-width: 992px) {

}

.reveal {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1),
              transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: var(--reveal-delay, 0ms);

  &.is-visible {
    opacity: 1;
    transform: translateY(0);
  }
}

.harvest-topbar {
  position: sticky;
  top: 0;
  z-index: 50;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(16px) saturate(1.4);
  -webkit-backdrop-filter: blur(16px) saturate(1.4);
  border-bottom: 1px solid rgba(233, 236, 239, 0.8);

  .topbar-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    max-width: 1100px;
    margin: 0 auto;
  }

  .topbar-center {
    flex: 1;
    text-align: center;
  }

  .topbar-eyebrow {
    display: inline-block;
    padding: 4px 14px;
    background: rgba(3, 160, 84, 0.08);
    color: $leaf;
    border-radius: $r-pill;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.02em;
  }

  .topbar-actions {
    display: flex;
    gap: 8px;
  }

  .topbar-pill {
    width: 38px;
    height: 38px;
    border: 1px solid rgba(233, 236, 239, 0.9);
    background: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: $ink;
    font-size: 0.95rem;
    transition: all 0.25s ease;

    &:active { transform: scale(0.92); }

    &:hover {
      box-shadow: $shadow-warm-sm;
      border-color: $leaf-soft;
      color: $leaf;
    }

    &.is-fav {
      background: $berry;
      border-color: $berry;
      color: #fff;
    }
  }
}

.hero-stage {
  padding: 16px;
}

.hero-frame {
  position: relative;
}

.hero-canvas {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  max-height: 520px;
  border-radius: $r-lg;
  overflow: hidden;
  background: linear-gradient(135deg, $cream-deep, #fff);
  box-shadow: $shadow-warm-lg;
  cursor: pointer;

  .hero-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover .hero-image {
    transform: scale(1.04);
  }

  .hero-veil {
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(0,0,0,0.25) 0%, transparent 25%, transparent 60%, rgba(42, 37, 32, 0.35) 100%);
    pointer-events: none;
  }

  .hero-frame-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 2;
  }

  .frame-svg {
    width: 100%;
    height: 100%;
  }

  .frame-logo {
    position: absolute;
    bottom: 25px;
    left: 35px;
    opacity: 0.7;
  }
}

.hero-tags {
  position: absolute;
  top: 14px;
  right: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 3;

  .tag {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 11px;
    border-radius: $r-pill;
    font-size: 0.7rem;
    font-weight: 700;
    color: #fff;
    backdrop-filter: blur(8px);
    box-shadow: 0 4px 14px rgba(0,0,0,0.18);

    i { font-size: 0.65rem; }

    &.tag-hot     { background: rgba(192, 57, 43, 0.92); }
    &.tag-special { background: rgba(232, 168, 56, 0.95); color: $ink; }
    &.tag-instant { background: rgba(26, 107, 60, 0.92); }
    &.tag-presale { background: rgba(131, 56, 232, 0.95); }
    &.tag-zara { background: #ca8a04; color: #fff; }
  }
}

.freshness-seal {
  position: absolute;
  bottom: 14px;
  left: 14px;
  width: 78px;
  height: 78px;
  z-index: 3;
  filter: drop-shadow(0 6px 14px rgba(0,0,0,0.3));

  .seal-rotate {
    width: 100%;
    height: 100%;
    animation: seal-spin 18s linear infinite;

    .seal-text {
      font-size: 9.5px;
      font-weight: 600;
      fill: #fff;
      letter-spacing: 0.5px;
    }
  }

  .seal-core {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 36px;
    height: 36px;
    background: $leaf;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 1rem;
    box-shadow: inset 0 0 0 2px rgba(255,255,255,0.3);
  }
}

@keyframes seal-spin {
  to { transform: rotate(360deg); }
}

.hero-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(6px);
  box-shadow: 0 6px 18px rgba(0,0,0,0.15);
  color: $ink;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  transition: all 0.25s;

  &:hover { background: #fff; transform: translateY(-50%) scale(1.08); }
  &:active { transform: translateY(-50%) scale(0.95); }

  &.hero-arrow-prev { right: 14px; }
  &.hero-arrow-next { left: 14px; }
}

.hero-counter {
  position: absolute;
  bottom: 16px;
  right: 14px;
  display: flex;
  align-items: baseline;
  gap: 2px;
  padding: 5px 12px;
  background: rgba(42, 37, 32, 0.72);
  backdrop-filter: blur(8px);
  border-radius: $r-pill;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  z-index: 3;

  .counter-current { font-size: 0.92rem; color: $sun; }
  .counter-divider { opacity: 0.6; margin: 0 2px; }
  .counter-total   { opacity: 0.8; }
}

.thumb-reel {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  padding: 4px;
  overflow-x: auto;
  direction: ltr;
  scroll-snap-type: x mandatory;
  &::-webkit-scrollbar { height: 0; }

  .thumb {
    position: relative;
    flex: 0 0 64px;
    width: 64px;
    height: 64px;
    padding: 0;
    border: 2px solid transparent;
    border-radius: $r-sm;
    overflow: hidden;
    cursor: pointer;
    background: #fff;
    transition: all 0.25s;
    scroll-snap-align: start;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .thumb-glow {
      position: absolute;
      inset: 0;
      border-radius: $r-sm - 2;
      box-shadow: inset 0 0 0 0 $sun;
      transition: box-shadow 0.25s;
    }

    &.is-active {
      border-color: $sun;
      transform: translateY(-2px);
      box-shadow: $shadow-warm-sm;

      .thumb-glow { box-shadow: inset 0 0 0 2px rgba(232, 168, 56, 0.3); }
    }
  }
}

.title-block {
  padding: 8px 20px 4px;
}

.title-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  color: $leaf;
  margin-bottom: 6px;

  i { font-size: 0.7rem; }
}

.title-main {
  font-size: 1.65rem;
  font-weight: 800;
  line-height: 1.3;
  color: $ink;
  margin: 0;
  letter-spacing: -0.01em;
}

.title-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

/* Edge-triggered product navigation */
.edge-nav {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: 90;
  display: none;
  align-items: center;
  pointer-events: none;

  &--left {
    left: 0;
    flex-direction: row;
  }

  &--right {
    right: 0;
    flex-direction: row-reverse;
  }

  &__trigger {
    width: 30px;
    height: 600px;
    flex-shrink: 0;
    pointer-events: auto;
    cursor: pointer;
  }

  &__btn {
    pointer-events: auto;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    border: 1.5px solid rgba(226, 214, 189, 0.7);
    background: #fff;
    color: $ink;
    border-radius: $r-pill;
    font-family: inherit;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    box-shadow: $shadow-warm-sm;
    opacity: 0;
    transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.16, 1, 0.3, 1),
                border-color 0.22s, color 0.22s, box-shadow 0.22s;

    i {
      font-size: 0.8rem;
      transition: transform 0.25s;
    }

    /* hidden state */
    .edge-nav--left & {
      transform: translateX(-8px);
    }
    .edge-nav--right & {
      transform: translateX(8px);
    }
  }

  &:hover .edge-nav__btn {
    opacity: 1;
    .edge-nav--left & { transform: translateX(4px); }
    .edge-nav--right & { transform: translateX(-4px); }
  }

  .edge-nav__btn:hover {
    border-color: $leaf-soft;
    color: $leaf;
    box-shadow: $shadow-warm-md;
    transform: translateY(-1px) !important;

    i { transform: translateX(-3px); }
  }

  .edge-nav--right .edge-nav__btn:hover i { transform: translateX(3px); }

  .edge-nav__btn:active { transform: scale(0.96) !important; }
}

@media (hover: none) {
  .edge-nav {
    &__trigger { width: 56px; height: 140px; }
    .edge-nav__btn {
      opacity: 0;
      transform: translateX(0) !important;
      padding: 14px 10px;
      font-size: 0.75rem;
      border-radius: 0;
      background: rgba(255, 255, 255, 0.92);
      backdrop-filter: blur(6px);
      pointer-events: none;
      transition: opacity 0.22s ease;
    }

    &--active .edge-nav__btn {
      opacity: 1;
      pointer-events: auto;
    }
  }
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(226, 214, 189, 0.6);
  border-radius: $r-pill;
  font-size: 1.05rem;
  color: $ink-soft;
  font-weight: 500;

  i { color: $leaf-soft; font-size: 1.05rem; }
  .meta-value { font-weight: 700; color: $ink; }

  .stars {
    display: inline-flex;
    gap: 1px;

    i {
      color: #d8cba8;
      font-size: 1.05rem;

      &.star-on { color: $sun; }
    }
  }
}

.price-card {
  position: relative;
  margin: 20px 16px 0;
  border-radius: $r-lg;
  overflow: hidden;
  box-shadow: $shadow-leaf;
  min-height: 920px;
}

.price-card-bg {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(135deg, $leaf-deep 0%, $leaf 60%, $leaf-soft 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle at 85% 15%, rgba(232, 168, 56, 0.25) 0%, transparent 40%),
      radial-gradient(circle at 10% 90%, rgba(255,255,255,0.08) 0%, transparent 35%);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E");
    opacity: 0.12;
    mix-blend-mode: overlay;
  }
}

.price-card-inner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 22px 22px 18px;
}

.price-eyebrow {
  display: block;
  font-size: 0.72rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px;
  letter-spacing: 0.02em;
}

.price-figure {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.price-amount {
  font-size: 2.4rem;
  font-weight: 900;
  color: #fff;
  line-height: 1;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 12px rgba(0,0,0,0.18);
}

.price-currency {
  font-size: 0.92rem;
  font-weight: 600;
  color: rgba(255,255,255,0.85);
}

.price-sub {
  margin-top: 8px;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.7);
}

.price-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.price-status {
  padding: 5px 12px;
  border-radius: $r-pill;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  backdrop-filter: blur(4px);

  &.st-active   { background: rgba(255,255,255,0.95); color: $leaf; }
  &.st-pending  { background: rgba(232, 168, 56, 0.95); color: $ink; }
  &.st-inquiry  { background: rgba(255,255,255,0.85); color: $leaf-deep; }
  &.st-sold     { background: rgba(192, 57, 43, 0.95); color: #fff; }
  &.st-inactive { background: rgba(255,255,255,0.5); color: rgba(255,255,255,0.8); }
}

.price-updated {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.7rem;
  color: rgba(255,255,255,0.7);
}
.price-chart{
    position: relative;
    padding: 25px 0px;
}

// Exportable Products Currency Conversion
.exportable-currency {
  position: relative;
  z-index: 1;
  padding: 16px 22px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  margin: 0 22px;
  border-radius: $r-md;
  margin-top: 12px;
  margin-bottom: 12px;

  .exportable-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    background: linear-gradient(135deg, $sun, $sun-deep);
    color: #fff;
    border-radius: $r-pill;
    font-size: 0.85rem;
    font-weight: 700;
    margin-bottom: 14px;
    box-shadow: 0 4px 14px rgba(232, 168, 56, 0.3);

    i {
      font-size: 0.9rem;
    }
  }

  .currency-conversion {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .conversion-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: $r-sm;
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.25s;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
      border-color: rgba(255, 255, 255, 0.4);
      transform: translateX(-4px);
    }

    .conversion-label {
      font-size: 0.9rem;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
    }

    .conversion-value {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 1.05rem;
      font-weight: 800;
      color: #fff;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

      .currency-flag {
        font-size: 1.2rem;
      }
    }
  }

  .currency-loading {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: $r-sm;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;

    i {
      color: $sun;
      font-size: 1rem;
    }
  }

  .currency-error {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: rgba(192, 57, 43, 0.2);
    border-radius: $r-sm;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.85rem;
    border: 1px solid rgba(192, 57, 43, 0.4);

    i {
      color: #e74c3c;
    }
  }
}

.price-note {
  position: relative;
  z-index: 1;
  display: flex;
  gap: 10px;
  padding: 14px 22px;
  background: rgba(0,0,0,0.18);
  backdrop-filter: blur(4px);
  border-top: 1px solid rgba(255,255,255,0.08);

  i {
    color: $sun;
    font-size: 0.9rem;
    flex-shrink: 0;
    margin-top: 2px;
  }

  p {
    margin: 0;
    font-size: 1.04rem;
    line-height: 1.6;
    color: rgba(255,255,255,0.85);
  }
}

.asside-section{
  position: absolute;
  top:680px;
  width: 50%;
}
.origin-story {
  margin: 0px 16px 0;
  padding: 26px 22px 22px;
  background: #fff;
  border-radius: $r-lg;
  box-shadow: $shadow-warm-md;
  border: 1px solid rgba(226, 214, 189, 0.5);
  overflow: hidden;
  min-height: 350px;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 120px;
    height: 120px;
    background: radial-gradient(circle, rgba(232, 168, 56, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }
}

.origin-deco {
  position: absolute;
  top: -10px;
  left: -10px;
  width: 80px;
  height: 80px;
  color: rgba(26, 107, 60, 0.06);
  transform: rotate(-15deg);

  svg { width: 100%; height: 100%; }
}

.origin-quote {
  position: relative;
  font-size: 0.92rem;
  line-height: 1.85;
  color: $ink;
  margin: 0 0 20px;

  strong {
    color: $leaf;
    font-weight: 700;
  }
}

.origin-author {
  display: flex;
  flex-direction: row-reverse;  gap: 12px;
  padding-top: 18px;
}

.origin-author-top {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 18px;
}

.author-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin-bottom: 10px;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(135deg, $leaf-soft, $leaf);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 1.4rem;
  box-shadow: 0 4px 12px rgba(26, 107, 60, 0.25);

  img { width: 100%; height: 100%; object-fit: cover; }
}

.author-meta {
  flex: 1;
  display: flex;
  flex-direction: column;

  .author-name { font-size: 1.2rem; font-weight: 700; color: $ink; }
  .author-role { font-size: 1rem; color: $ink-soft; }
}

.origin-call {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 15px 15px;
  color: #fff;
  border: none;
  border-radius: 0.55rem;
  font-size: 1.2rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(26, 107, 60, 0.3);
  transition: all 0.25s;
  background-color: $secondary;

  &:hover { background: $leaf-deep; transform: translateY(-1px); }
  &:active { transform: scale(0.96); }
}

.origin-report {
  background: transparent !important;
  color: $ink-soft !important;
  box-shadow: none !important;
  width: 38px;
  padding: 8px !important;
  justify-content: center;
  border: 1px solid $line !important;

  &:hover {
    color: $berry !important;
    border-color: $berry !important;
    background: rgba(220, 53, 69, 0.06) !important;
  }
}

.origin-stats {
  display: flex;
  align-items: center;
  justify-content: space-around;
  margin-top: 18px;

  .ostat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;

    .ostat-num {
      font-size: 1.25rem;
      font-weight: 800;
      color: $leaf;
      line-height: 1;

      small { font-size: 0.7rem; font-weight: 600; }
    }

    .ostat-label {
      font-size: 0.7rem;
      color: $ink-soft;
    }
  }

  .ostat-sep {
    width: 1px;
    height: 26px;
    background: $line;
  }
}

.origin-actions {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px dashed $line;

  .origin-actions-title {
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: $leaf-deep;
    margin-bottom: 14px;
    display: block;
  }

  .origin-actions-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  .origin-action-card {
    display: flex;
    flex-flow: row;
    align-items: center;
    gap: 12px;
    padding: 16px 18px;
    background: #fff;
    border: 1px solid rgba(226, 214, 189, 0.5);
    border-radius: $r-md;
    cursor: pointer;
    transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
    position: relative;
    overflow: hidden;
    @media (max-width: 480px) {
      flex-flow: column;
    }
    &:hover {
      transform: translateY(-3px);
      border-color: rgba(232, 168, 56, 0.4);
      box-shadow: $shadow-warm-md;
    }

    &:active {
      transform: scale(0.98);
    }

    .action-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      border-radius: $r-sm;
      font-size: 1.1rem;
      color: #fff;
      flex-shrink: 0;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.05) rotate(-3deg);
      }

      &.bid { background: linear-gradient(135deg, $berry, #c0392b); }
      &.inquiry { background: linear-gradient(135deg, $sun, #ca8a04); }
      &.contact { background: linear-gradient(135deg, $leaf, $leaf-deep); }
      &.chat { background: linear-gradient(135deg, #3b82f6, #2563eb); }
      &.supply { background: linear-gradient(135deg, $secondary, #4a90e2); }
      &.broadcast { background: linear-gradient(135deg, #8b5cf6, #7c3aed); }
    }

    .action-info {
      display: flex;
      flex-direction: column;
      gap: 3px;
      flex: 1;
      min-width: 0;

      .action-title {
        font-size: 0.95rem;
        font-weight: 700;
        color: $ink;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .action-cta {
        font-size: 0.82rem;
        color: $ink-soft;
        line-height: 1.5;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }
  }
}

.services-section {
  margin: 15px 16px 0;
  padding: 32px 0px 26px;
  border-radius: $r-lg;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    bottom: -40px;
    right: -40px;
    width: 140px;
    height: 140px;
    background: radial-gradient(circle, rgba(3, 160, 84, 0.06) 0%, transparent 70%);
    pointer-events: none;
  }
}

.services-header {
  text-align: center;
  position: relative;
  z-index: 1;
}

.services-ornament {
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0 auto 14px;
  border-radius: 50%;
  background: linear-gradient(135deg, $leaf, $leaf-soft);
  color: #fff;
  box-shadow: 0 4px 14px rgba(3, 160, 84, 0.25);

  svg { width: 20px; height: 20px; }
}

.services-title {
  font-size: 1.15rem;
  font-weight: 800;
  color: $ink;
  margin: 0 0 8px;
}

.services-subtitle {
  font-size: 1.05rem;
  color: $ink-soft;
  margin: 0 0 16px;
  line-height: 1.6;
}

.services-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 22px;

  .divider-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: $leaf-soft;
  }

  .divider-line {
    width: 60px;
    height: 1.5px;
    border-radius: 2px;
    background: linear-gradient(90deg, transparent, $leaf-soft, transparent);
  }
}

.services-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  position: relative;
  z-index: 1;
  justify-items: center;
  width: 50%;
  margin: 0 auto;

  @media (max-width: 1024px) {
    width: 100%;
  }
}

.service-card {
  position: relative;
  padding: 20px 16px;
  background: #fff;
  border-radius: $r-md;
  border: 1px solid rgba(226, 214, 189, 0.5);
  box-shadow: $shadow-warm-sm;
  width: 100%;
  cursor: default;
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: $shadow-warm-md;
    border-color: rgba(232, 168, 56, 0.4);

    .card-icon-wrap {
      background: linear-gradient(135deg, $sun, $sun-deep);
      transform: scale(1.08);
    }
  }
}

.card-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: $r-sm;
  background: linear-gradient(135deg, $leaf, $leaf-soft);
  color: #fff;
  font-size: 1.1rem;
  margin-bottom: 14px;
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.card-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: $ink;
  margin: 0 0 6px;
}

.card-desc {
  font-size: 0.92rem;
  line-height: 1.65;
  color: $ink-soft;
  margin: 0;
}

.services-cta {
  text-align: center;
  margin-top: 22px;
  position: relative;
  z-index: 1;
}

.services-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 28px;
  background: linear-gradient(135deg, $leaf, $leaf-soft);
  color: #fff;
  border: none;
  border-radius: $r-pill;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: $shadow-leaf;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  i { font-size: 0.85rem; transition: transform 0.3s; }

  &:hover {
    background: linear-gradient(135deg, $leaf-deep, $leaf);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(3, 160, 84, 0.3);

    i { transform: translateX(-4px); }
  }

  &:active { transform: scale(0.97); }
}

.narrative,
.specs,
.breadcrumbs {
  padding: 24px 20px 0;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;

  .label-mark {
    width: 4px;
    height: 18px;
    border-radius: 2px;
  }

  h3 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 800;
    color: $ink;
  }
}

.narrative-body {
  position: relative;
  font-size: 0.9rem;
  line-height: 1.85;
  color: $ink;

  p { margin: 0; }

  &.is-collapsed {
    max-height: 5.6em;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2em;
      pointer-events: none;
    }
  }
}

.narrative-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 0;
  border: none;
  background: none;
  color: $leaf;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;

  i { font-size: 0.7rem; transition: transform 0.25s; }
  &:hover { color: $leaf-deep; }
}
.specs-table {
  background: #fff;
  border-radius: $r-md;
  overflow: hidden;
  box-shadow: $shadow-warm-sm;
  border: 1px solid rgba(226, 214, 189, 0.5);
}

.spec-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 13px 16px;
  border-bottom: 1px solid rgba(226, 214, 189, 0.5);
  transition: background 0.2s;

  &:last-child { border-bottom: none; }

  &:nth-child(odd) { background: rgba(243, 235, 218, 0.3); }

  &:hover { background: rgba(232, 168, 56, 0.06); }

  .spec-key {
    font-size: 1.02rem;
    color: $ink-soft;
    font-weight: 500;
  }

  .spec-val {
    font-size: 1.05rem;
    color: $ink;
    font-weight: 700;
    text-align: left;

    &.spec-link {
      color: $leaf;
      text-decoration: none;
      border-bottom: 1px dashed rgba(3, 160, 84, 0.4);
      transition: all 0.2s;

      &:hover {
        color: $leaf-deep;
        border-bottom-style: solid;
      }
    }
  }
}
.crumb-trail {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;

  .crumb {
    padding: 5px 0px;
    font-size: 0.76rem;
    color: $ink-soft;
    text-decoration: none;
    transition: all 0.2s;

    &:hover { color: $leaf; border-color: $leaf-soft; }

    &.crumb-current {
      background: $leaf;
      color: #fff;
      border-color: $leaf;
      font-weight: 600;
    }
  }

  .crumb-sep {
    font-size: 0.6rem;
    color: $ink;
  }
}

.similar {
  padding: 24px 20px 0;
  width: 80%;
  margin: 0 auto;
}

.similar-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;

  .section-label { margin-bottom: 0; }

  .similar-all {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.78rem;
    font-weight: 600;
    color: $leaf;
    text-decoration: none;

    i { font-size: 0.65rem; }
    &:hover { color: $leaf-deep; }
  }
}

.similar-rail {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 6px;
  scroll-snap-type: x mandatory;
  justify-content: center;
  &::-webkit-scrollbar { height: 0; }
}

.similar-item {
  flex: 0 0 160px;
  background: #fff;
  border-radius: $r-md;
  overflow: hidden;
  cursor: pointer;
  scroll-snap-align: start;
  box-shadow: $shadow-warm-sm;
  border: 1px solid rgba(226, 214, 189, 0.4);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: $shadow-warm-md;
    border-color: $sun;
  }

  .similar-image {
    width: 100%;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    background: $cream-deep;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s;
    }
  }

  &:hover .similar-image img { transform: scale(1.08); }

  .similar-info {
    padding: 12px;

    h4 {
      margin: 0 0 6px;
      font-size: 0.82rem;
      font-weight: 700;
      color: $ink;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .similar-price {
      font-size: 0.88rem;
      font-weight: 800;
      color: $leaf;

      small { font-size: 0.65rem; font-weight: 500; color: $ink-soft; }
    }
  }
}
.cta-banner {
  padding: 0 16px;
  max-width: 1100px;
  margin: 24px auto 0;
}

.cta-inner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  background: linear-gradient(135deg,#FFEB3B, #FF9800);
  border-radius: $r-lg;
  border: 1px solid rgba(3, 160, 84, 0.15);
  box-shadow: $shadow-warm-sm;
  flex-wrap: wrap;
}

.cta-text {
  flex: 1;
  min-width: 200px;

  p {
    margin: 0;
    font-size: 1.02rem;
    line-height: 1.7;
    color: $ink-soft;

    strong { color: $leaf; font-weight: 700; }
  }
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  background: linear-gradient(135deg, $leaf, $leaf-soft);
  color: #fff;
  border: none;
  border-radius: $r-pill;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: $shadow-leaf;
  transition: all 0.25s;

  svg { flex-shrink: 0; }

  &:hover { background: linear-gradient(135deg, $leaf-deep, color.adjust($leaf-soft, $lightness: -4%)); }
  &:active { transform: scale(0.97); }
}

.content-note {
  padding: 0 16px;
  max-width: 1100px;
  margin: 24px auto 0;
}

.note-frame {
  position: relative;
  padding: 26px 28px 22px;
  background: linear-gradient(155deg, #fff 0%, #fdfaf3 55%, rgba(243, 235, 218, 0.55) 100%);
  border-radius: $r-lg;
  border: 1px solid rgba(226, 214, 189, 0.6);
  box-shadow: $shadow-warm-sm;
  overflow: hidden;

  .note-veil {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(circle at 92% 8%, rgba(232, 168, 56, 0.14) 0%, transparent 42%),
      radial-gradient(circle at 4% 96%, rgba(3, 160, 84, 0.1) 0%, transparent 40%);
  }

  .note-corner {
    position: absolute;
    width: 46px;
    height: 46px;
    border-color: rgba(232, 168, 56, 0.4);
    border-style: solid;
    border-width: 0;
    pointer-events: none;

    &.note-corner-tr {
      top: 10px;
      left: 10px;
      border-top-width: 2px;
      border-left-width: 2px;
      border-top-left-radius: 8px;
    }

    &.note-corner-bl {
      bottom: 10px;
      right: 10px;
      border-bottom-width: 2px;
      border-right-width: 2px;
      border-bottom-right-radius: 8px;
    }
  }
}

.note-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.note-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 13px;
  background: rgba(3, 160, 84, 0.08);
  color: $leaf;
  border-radius: $r-pill;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.01em;

  i { font-size: 0.72rem; }
}

.note-shuffle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid rgba(226, 214, 189, 0.7);
  background: #fff;
  color: $ink-soft;
  border-radius: $r-pill;
  font-family: inherit;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s;

  i { font-size: 0.72rem; transition: transform 0.4s ease; }

  &:hover {
    border-color: $leaf-soft;
    color: $leaf;
    box-shadow: $shadow-warm-sm;

    i { transform: rotate(-180deg); }
  }

  &:active { transform: scale(0.95); }
}

.note-text {
  position: relative;
  margin: 0;
  font-size: 0.94rem;
  line-height: 2;
  color: $ink;
  text-align: justify;
  text-align-last: right;
}

.note-inline {
  text-decoration: none;
  font-weight: 700;
  border-bottom: 1px solid rgba(232, 168, 56, 0.5);
  transition: all 0.2s;

  &:hover { border-bottom-color: $leaf; }

  &.note-inline-product,
  &.note-inline-location,
  &.note-inline-type {
    color: $leaf;
    &:hover { color: $leaf-deep; }
  }
}

.note-inline-call {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 6px;
  border: 1px solid rgba(232, 168, 56, 0.5);
  border-radius: $r-sm;
  background: rgba(232, 168, 56, 0.1);
  color: $sun-deep;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 800;
  line-height: 1.7;
  cursor: pointer;
  transition: all 0.25s;

  &::before {
    content: '\f095';
    font-family: 'Font Awesome 6 Free';
    font-weight: 900;
    font-size: 0.75rem;
  }

  &:hover {
    background: $leaf;
    border-color: $leaf;
    color: #fff;
    box-shadow: $shadow-leaf;
  }
}

.note-footer {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed rgba(226, 214, 189, 0.8);
}

.note-footmark {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  color: $leaf;

  i { font-size: 0.68rem; }
}

.faq-section {
  padding: 0 16px;
  max-width: 1100px;
  margin: 24px auto 0;
}

.faq-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  text-align: center;
}

.faq-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 13px;
  background: rgba(232, 168, 56, 0.12);
  color: $sun-deep;
  border-radius: $r-pill;
  font-size: 0.74rem;
  font-weight: 700;

  i { font-size: 0.72rem; }
}

.faq-title {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 800;
  color: $ink;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.faq-item {
  background: #fff;
  border: 1px solid rgba(226, 214, 189, 0.6);
  border-radius: $r-md;
  box-shadow: $shadow-warm-sm;
  overflow: hidden;
  transition: border-color 0.25s, box-shadow 0.25s;

  &.is-open {
    border-color: rgba(3, 160, 84, 0.35);
    box-shadow: 0 6px 22px rgba(26, 107, 60, 0.08);
  }
}

.faq-question {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 18px;
  background: none;
  border: none;
  font-family: inherit;
  font-size: 0.98rem;
  font-weight: 700;
  color: $ink;
  text-align: right;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: $leaf;

    .faq-q-mark {
      background: $leaf;
      color: #fff;
    }
  }
}

.faq-q-mark {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(232, 168, 56, 0.14);
  color: $sun-deep;
  font-size: 0.8rem;
  transition: all 0.25s;
}

.faq-q-text {
  flex: 1;
  min-width: 0;
}

.faq-chevron {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  color: $ink-soft;
  transition: transform 0.3s ease;

  i { font-size: 0.72rem; }
}

.faq-item.is-open .faq-chevron {
  transform: rotate(180deg);
  color: $leaf;
}

.faq-answer {
  overflow: hidden;
  transition: max-height 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.faq-answer-inner {
  padding: 0 18px 16px;
  font-size: 0.9rem;
  line-height: 1.9;
  color: $ink-soft;
  text-align: justify;
  text-align-last: right;

  p { margin: 0; }
}

.seo-article {
  padding: 32px 20px 0;
  max-width: 820px;
  margin: 0 auto;

  .article-inner {
    background: #fff;
    border-radius: $r-lg;
    padding: 28px 32px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  }

  .article-title {
    font-size: 1.25rem;
    font-weight: 800;
    color: $ink;
    margin: 0 0 16px;
    line-height: 1.5;
  }

  .article-body {
    font-size: 0.92rem;
    line-height: 1.9;
    color: $ink-soft;

    p {
      margin: 0 0 14px;

      &:last-child { margin-bottom: 0; }
    }

    strong {
      color: $ink;
      font-weight: 700;
    }
  }
}

@media (min-width: 768px) {
  .cta-banner { padding: 0 24px; }
  .cta-inner { padding: 20px 28px; }
  .content-note,
  .faq-section { padding: 0 24px; }
}

.bottom-spacer { height: 96px; }

.action-dock {
  position: fixed;
  display: none;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 40;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  background: linear-gradient(180deg, rgba(245, 245, 245, 0) 0%, rgba(245, 245, 245, 0.95) 30%);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  pointer-events: none;

  .dock-inner {
    pointer-events: auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    max-width: 560px;
    margin: 0 auto;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(20px) saturate(1.5);
    -webkit-backdrop-filter: blur(20px) saturate(1.5);
    border: 1px solid rgba(226, 214, 189, 0.6);
    border-radius: $r-lg;
    box-shadow: 0 12px 40px rgba(120, 80, 20, 0.18);
  }

  .dock-price {
    display: flex;
    flex-direction: column;

    .dock-amount {
      font-size: 1.3rem;
      font-weight: 900;
      color: $leaf;
      line-height: 1;
    }

    .dock-currency {
      font-size: 0.72rem;
      color: $ink-soft;
      font-weight: 500;
    }
  }

  .dock-actions {
    display: flex;
    gap: 8px;
  }

}
.dock-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 11px 50px;
  border: none;
  border-radius: $r-pill;
  font-size: 0.86rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.25s;

  i { font-size: 0.85rem; }

  &.dock-chat {
    background: rgba(26, 107, 60, 0.1);
    color: $leaf;

    &:hover { background: rgba(26, 107, 60, 0.18); }
  }

  &.dock-order {
    background: linear-gradient(135deg, $olive, $olive-dark);
    color: #fff;
    box-shadow: 0 6px 16px rgba(232, 168, 56, 0.4);
    margin-right: auto;


    &:hover { transform: translateY(-1px); box-shadow: 0 10px 22px rgba(232, 168, 56, 0.5); }
  }

  &:active { transform: scale(0.96); }
}

.harvest-loading,
.harvest-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  padding: 32px;
  text-align: center;
  background: $cream-deep;
  color: $ink-soft;
  font-family: 'IRANYekan', 'Vazirmatn', sans-serif;
}

.loading-orb,
.error-orb {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  margin-bottom: 18px;
}

.loading-orb {
  background: rgba(26, 107, 60, 0.1);
  color: $leaf;
  animation: orb-pulse 1.4s ease-in-out infinite;
}

.error-orb {
  background: rgba(192, 57, 43, 0.1);
  color: $berry;
}

@keyframes orb-pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(26, 107, 60, 0.3); }
  50%      { transform: scale(1.08); box-shadow: 0 0 0 16px rgba(26, 107, 60, 0); }
}

.harvest-loading p { font-size: 0.88rem; }

.harvest-error {
  h4 { color: $ink; margin: 0 0 8px; font-size: 1.1rem; }
  p  { font-size: 0.85rem; margin-bottom: 18px; }

  .retry-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 22px;
    background: $leaf;
    color: #fff;
    border: none;
    border-radius: $r-pill;
    font-size: 0.86rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    box-shadow: $shadow-leaf;

    &:hover { background: $leaf-deep; }
    &:active { transform: scale(0.97); }
  }
}

// ── Video player ──
.video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;

  .hero-video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .video-play-btn {
    position: absolute;
    bottom: 16px;
    left: 16px;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 50%;
    background: rgba(0,0,0,0.6);
    color: #fff;
    font-size: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.25s;
    z-index: 4;

    &:hover {
      background: rgba(0,0,0,0.8);
      transform: scale(1.1);
    }
  }
}

.thumb-video-badge {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.3);
  color: #fff;
  font-size: 0.75rem;
  pointer-events: none;
}

// ── Lightbox ──
.lightbox-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0,0,0,0.92);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  direction: ltr;

  .lightbox-close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
    color: #fff;
    font-size: 1.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: all 0.25s;

    &:hover { background: rgba(255,255,255,0.2); }
  }

  .lightbox-toolbar {
    position: absolute;
    top: 16px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: rgba(255,255,255,0.08);
    border-radius: 999px;
    z-index: 10;
    backdrop-filter: blur(12px);

    .lb-btn {
      width: 36px;
      height: 36px;
      border: none;
      border-radius: 50%;
      background: transparent;
      color: rgba(255,255,255,0.8);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.9rem;
      transition: all 0.2s;

      &:hover { background: rgba(255,255,255,0.15); color: #fff; }
      &:disabled { opacity: 0.3; cursor: default; }
    }

    .lb-zoom-level {
      color: rgba(255,255,255,0.7);
      font-size: 0.78rem;
      min-width: 44px;
      text-align: center;
      font-variant-numeric: tabular-nums;
    }

    .lb-sep {
      width: 1px;
      height: 24px;
      background: rgba(255,255,255,0.15);
      margin: 0 4px;
    }
  }

  .lightbox-media {
    max-width: 95vw;
    max-height: 80vh;
    border-radius: 8px;
  }

  .lightbox-image-wrap {
    overflow: hidden;
    max-width: 95vw;
    max-height: 80vh;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    position: relative;

    .lightbox-image {
      max-width: 100%;
      max-height: 80vh;
      object-fit: contain;
      transition: transform 0.15s ease;
      border-radius: 8px;
      will-change: transform;
    }
  }

  .lightbox-frame-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.2);
  }

  .lb-frame-logo {
    position: absolute;
    bottom: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    opacity: 0.6;
    filter: drop-shadow(0 1px 4px rgba(0,0,0,0.3));
  }

  .lightbox-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 48px;
    height: 48px;
    border: none;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
    color: #fff;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: all 0.25s;
    backdrop-filter: blur(8px);

    &:hover { background: rgba(255,255,255,0.2); }

    &.lightbox-prev { left: 16px; }
    &.lightbox-next { right: 16px; }

    @media (max-width: 767px) {
      width: 40px;
      height: 40px;
      font-size: 1rem;

      &.lightbox-prev { left: 8px; }
      &.lightbox-next { right: 8px; }
    }
  }

  .lightbox-counter {
    position: absolute;
    bottom: 96px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(255,255,255,0.7);
    font-size: 0.85rem;
    font-weight: 600;
    padding: 6px 16px;
    background: rgba(0,0,0,0.5);
    border-radius: 999px;
    backdrop-filter: blur(8px);
  }

  .lightbox-thumbs {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 8px;
    padding: 8px 12px;
    background: rgba(255,255,255,0.06);
    border-radius: 12px;
    overflow-x: auto;
    max-width: 90vw;
    backdrop-filter: blur(12px);
    direction: ltr;

    &::-webkit-scrollbar { height: 0; }

    .lb-thumb {
      flex: 0 0 52px;
      width: 52px;
      height: 52px;
      padding: 0;
      border: 2px solid transparent;
      border-radius: 8px;
      overflow: hidden;
      cursor: pointer;
      background: rgba(255,255,255,0.1);
      transition: all 0.25s;
      position: relative;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      &.is-active {
        border-color: $sun;
        transform: translateY(-2px);
      }

      .lb-thumb-video {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0,0,0,0.35);
        color: #fff;
        font-size: 0.7rem;
      }
    }
  }
}

.lightbox-enter-active,
.lightbox-leave-active {
  transition: opacity 0.25s ease;
}
.lightbox-enter-from,
.lightbox-leave-to {
  opacity: 0;
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

// Share sheet
.share-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: rgba(42, 37, 32, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));

  @media (min-width: 640px) {
    align-items: center;
    padding: 24px;
  }
}

.share-sheet {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: $r-lg;
  box-shadow: 0 24px 60px rgba(42, 37, 32, 0.25);
  overflow: hidden;
  transform-origin: bottom center;
}

.share-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 0;
}

.share-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: $ink;
}

.share-close {
  width: 34px;
  height: 34px;
  border: 1px solid rgba(226, 214, 189, 0.6);
  background: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $ink-soft;
  font-size: 0.85rem;
  transition: all 0.2s;

  &:hover {
    border-color: $berry;
    color: $berry;
    background: rgba(220, 53, 69, 0.06);
  }
}

.share-actions {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 18px 20px 22px;
}

.share-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 14px 8px;
  border: 1.5px solid rgba(226, 214, 189, 0.5);
  background: #fff;
  border-radius: $r-md;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    transform: translateY(-3px);
    box-shadow: $shadow-warm-md;
  }

  &:active {
    transform: scale(0.96);
  }
}

.share-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.3rem;
  transition: all 0.25s;
}

.share-icon-whatsapp {
  background: linear-gradient(135deg, #25d366, #128c7e);
  box-shadow: 0 6px 16px rgba(37, 211, 102, 0.35);
}

.share-icon-telegram {
  background: linear-gradient(135deg, #00a4e4, #0088cc);
  box-shadow: 0 6px 16px rgba(0, 164, 228, 0.35);
}

.share-icon-copy {
  background: linear-gradient(135deg, $leaf-soft, $leaf);
  box-shadow: 0 6px 16px rgba(3, 160, 84, 0.35);

  &.is-copied {
    background: linear-gradient(135deg, $sun, $sun-deep);
    box-shadow: 0 6px 16px rgba(232, 168, 56, 0.4);
  }
}

.share-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: $ink;
  text-align: center;
  line-height: 1.3;
}

.share-sheet-enter-active,
.share-sheet-leave-active {
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.share-sheet-enter-from,
.share-sheet-leave-to {
  opacity: 0;
  transform: translateY(40px) scale(0.96);
}

.share-sheet-enter-to,
.share-sheet-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

@media (min-width: 768px) and (max-width: 1000px) {

  .harvest-detail {
    .hero-stage,
    .title-block,
    .narrative,
    .specs,
    .breadcrumbs,
    .similar,
    .seo-article {
      max-width: 640px;
      margin-left: auto;
      margin-right: auto;
    }

    .price-card,
    .origin-story {
      max-width: 608px;
      margin-left: auto;
      margin-right: auto;
    }

    .title-main { font-size: 2rem; }
    .price-amount { font-size: 2.6rem; }
  }
}

@media (max-width: 480px) {
  .dock-order{
    width: 100%;
    display: block;
  }
}
@media (max-width: 1000px) {
  .similar {
  width: 100%;
  }
  .seo-article {
    padding: 24px 12px 0;

    .article-inner {
      padding: 20px 16px;
    }

    .article-title {
      font-size: 1.05rem;
    }
  }
  .services-ornament {
  display: flex;
  }
  .asside-section {
    margin-top: 10px;
    position: relative;
    top: 0;
    width: 100%;

  }
  .dock-btn {
    padding: 11px 18px;
  }
  .author-meta{
    flex-direction: row;
    align-items: end;
    
    .author-name { font-size: 0.9rem;  }
    .author-role { font-size: 0.8rem; }
  }
}
@media (min-width: 1000px) {
  .harvest-detail {
    .title-main { font-size: 2.1rem; }
    .price-amount { font-size: 2.8rem; }
  }
    .detail-shell {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 36px;
    align-items: start;
    padding: 16px 24px 0;

    .detail-aside {
      position: sticky;
      top: 20px;

      .hero-stage { padding: 0; }
      .hero-canvas {
        max-height: 560px;
        aspect-ratio: 1 / 1;
      }
    }

    .detail-main { min-width: 0; }


  }
}
</style>
